import {
  deleteMission,
  getMission,
  deleteQuestLine,
  listCompletions,
  type CompletionViewer,
  type QuestCompletion,
  publishMission,
  removeQuestCover,
  removeQuestLine,
  reportQuest,
  reportQuestLine,
  saveQuest,
  saveQuestLine,
  unsaveQuest,
  updateMission,
  uploadQuestCover,
  uploadQuestSpotThumbnailPending,
  uploadQuestSpotPhoto,
  deleteQuestSpotPhoto,
  uploadQuestPagePhoto,
} from "../api.ts";
import { mountAdSlot } from "../ads.ts";
import { mountLegalFooter } from "../legal-footer.ts";
import { formatDate, formatDistance, locale, t, type MessageKey } from "../i18n/index.ts";
import {
  DEFAULT_SPOT_RADIUS_M,
  MAX_MISSION_DESCRIPTION,
  MAX_MISSION_MANNERS,
  MAX_MISSION_STORY,
  MAX_SPOT_LABEL,
  spotPathDistanceM,
  sumEstMinutes,
} from "../../shared/mission-quest.ts";
import { routePlaceLabel } from "../../shared/place-label.ts";
import { coverDate } from "../../shared/quest-cover.ts";
import { normalizeQuestLine, QUEST_LINE_MAX, questLineProblem } from "../../shared/quest-line.ts";
import { REPORT_REASONS } from "../../shared/quest-report.ts";
import {
  routePublishBlockers,
  spotPublishBlockers,
  type PublishBlockerKey,
} from "../../shared/quest-publish.ts";
import {
  MAX_TIME_LIMIT_MIN,
  MIN_TIME_LIMIT_MIN,
  routeTraceProgress,
  routeTraceReady,
  settingsSteps,
  shownSpots,
  isRouteEndpointCheckpoint,
  snapToLine,
  spotTraceHasLocation,
  spotTraceReady,
  walkMinutes,
  type SettingsStep,
} from "../../shared/route-trace.ts";
import type { LngLat } from "../../shared/types.ts";
import { CoverPickError, cropCover } from "./cover-crop.ts";
import {
  confirmAdminPublishedSave,
  mountCollapseBanner,
  type CollapseBannerHandle,
} from "./editor-banner-collapse.ts";
import { button, choiceGroup, el, questBar, questViewWorkspace, questWorkspace } from "./dom.ts";
import { CLOSE_ICON_SVG, mapControlButton } from "../map-embed-controls.ts";
import {
  mountQuestEditorColumns,
  refreshSpotThumbPreview,
  syncQuestEditorListChrome,
  scrollQuestEditorSectionIntoView,
  type QuestEditorColumnsHost,
  type QuestEditorColumnsMount,
} from "./quest-editor-columns.ts";
import {
  QUEST_SECTION_INTRO,
  QUEST_SECTION_MANNERS,
  QUEST_SECTION_MAP,
  QUEST_SECTION_ROUTE,
  QUEST_SECTION_SETTINGS,
  QUEST_SECTION_SPOTS,
  QUEST_SECTION_STORY,
  QUEST_SECTION_TITLE,
  parseSpotSectionId,
  parsePhotoSectionId,
  parseBlockSectionId,
  blockSectionId,
  photoSectionId,
  resolveEditorSectionOrder,
  spotSectionId,
  type QuestOrderContext,
  type QuestSectionId,
} from "../../shared/quest-editor-order.ts";
import { paintQuestSaveHeart, questSaveHeartEnabled } from "./save-heart.ts";
import { createAdminPublishedBanner } from "./quest-route-banner.ts";
import {
  appendQuestBarOverflow,
  appendQuestBarPublish,
  appendQuestBarDraftSave,
  appendQuestBarSaveChanges,
  appendQuestBarAutosaveToggle,
  paintQuestBarPublish,
  paintQuestBarSaveChanges,
  type QuestBarPublishOpts,
} from "./quest-editor-bar.ts";
import {
  readQuestEditorAutosavePref,
  writeQuestEditorAutosavePref,
} from "./quest-editor-autosave-pref.ts";
import { RouteMap, type PinKind } from "./route-map.ts";
import { SpotLocationPicker } from "./spot-location-picker.ts";
import { SpotLocationEmbed } from "./spot-location-embed.ts";
import { buildSpotTracePreviewCard, spotTracePreviewSection } from "./quest-spot-preview.ts";
import { cropSpotThumbnail, SpotThumbPickError } from "./spot-thumb-crop.ts";
import { questSpotMapPinForSpotTrace } from "./quest-spot-map-layers.ts";
import { formatMinutes as duration, questSettingsSummary } from "./summary.ts";
import { questAutoSummaryText } from "./quest-auto-summary.ts";
import { applyQuestPageHead, mountRouteQuestExplainer, questMetaDescriptionFromQuest } from "./route-quest-explainer.ts";
import { readHomeBrowseReturn } from "../home/home-browse-persist.ts";
import { formatHomeBrowseUrl } from "../home/home-browse-url.ts";
import { publishedQuestViewHref, questEditPath, questRoutePath, questViewPath } from "./router.ts";
import {
  onRouteSaved,
  questRouteWindowName,
  readMapBlock,
  writeMapBlock,
} from "./route-sync.ts";
import { createDeferredCommit } from "../input/deferred-commit.ts";
import { renderPageBlock, uploadGalleryItemFile } from "./page-block-ui.ts";
import { createDefaultPageBlock, type PageBlockKind } from "../../shared/quest-page-blocks.ts";
import { questEditorDirtySnapshot, questEditorIsDirty, type QuestEditorDirtyOpts } from "./quest-editor-dirty.ts";
import { bindQuestEditorDirtyDomListeners } from "./quest-editor-state.ts";
import { normalizeQuestSpotImages, toWriteBody, type QuestDetail, type QuestPagePhoto, type QuestSpot, type QuestSpotImage } from "./types.ts";
import { withSpotThumbnailCacheBust } from "../../shared/spot-thumbnail.ts";

export interface RouteTraceOptions {
  root: HTMLElement;
  quest: QuestDetail;
  /** "view" is the player page alone (Preview as player, published quests). */
  mode: "edit" | "view";
  /** "inline" renders only the read-only quest page into `root` (the home detail panel). */
  layout?: "full" | "inline";
  onToast: (message: string) => void;
  signedIn?: () => boolean;
  onRequireSignIn?: () => void;
  onSavedChange?: (saved: boolean) => void;
  /** Home map: persist browse camera before opening `/quest/:id/route` in another tab. */
  onBeforeOpenRouteEditor?: () => void;
  /** Spot map selection changed (panel map or external home map). */
  onSpotMapSelectionChange?: (index: number | null) => void;
}

type Modal =
  | { kind: "settings"; step: number }
  | { kind: "title"; step: number }
  | { kind: "description" }
  | { kind: "story" }
  | { kind: "manners" }
  | { kind: "spots"; index: number | null };

type ChunkId = "route" | "settings" | "spots" | "title" | "description" | "mission" | "secrets" | "story" | "manners" | "decorate";
type TextKind = "description" | "story" | "manners";

const SAVE_DELAY_MS = 600;
const MAX_TITLE = 80;
const DONE_FIRST = 10;
const DONE_PAGE = 50;
const TEXT_MAX: Record<TextKind, number> = {
  description: MAX_MISSION_DESCRIPTION,
  story: MAX_MISSION_STORY,
  manners: MAX_MISSION_MANNERS,
};

/**
 * Route Trace quest editor. Left: the live quest page (blank until the creator adds
 * something). Right: an ordered list of chunks; each opens a window asking one thing.
 */
export class RouteTraceEditor {
  private opts: RouteTraceOptions;
  private q: QuestDetail;
  private mapBlock: boolean;
  private modal: Modal | null = null;
  private placing: PinKind | null = null;
  private placed = new Set<PinKind>();
  private map: RouteMap | null = null;
  private mapEl: HTMLElement | null = null;
  /** Live map inside the middle-column GPS Art route card. */
  private editorRouteMap: RouteMap | null = null;
  private editorRouteMapEl: HTMLElement | null = null;
  /** The route map standing in as the cover while there is no photo. */
  private coverMap: RouteMap | null = null;
  private coverMapEl: HTMLElement | null = null;
  /** The cropped photo shown while it uploads. */
  private coverPreview: string | null = null;
  private coverInput: HTMLInputElement | null = null;
  /** Kept across re-renders: a draft's cover is served no-store, so a new <img> would fetch it again. */
  private coverImg: HTMLImageElement | null = null;
  /** The title on the cover; kept across re-renders so typing in it keeps focus and caret. */
  private coverTitle: HTMLElement | null = null;
  /** "Completed by" on published pages; null until the first page is asked for. */
  private done: {
    total: number;
    items: QuestCompletion[];
    viewer: CompletionViewer | null;
    loading: boolean;
    failed: boolean;
  } | null = null;
  /** The viewer's "Leave a line" form; kept across re-renders so typing keeps focus. */
  private lineForm: { form: HTMLFormElement; input: HTMLInputElement; error: HTMLElement; editing: boolean } | null =
    null;
  private lineBusy = false;
  private pageEl!: HTMLElement;
  private guideEl!: HTMLElement;
  private editorEl!: HTMLElement;
  private paletteEl!: HTMLElement;
  private editorMount: QuestEditorColumnsMount | null = null;
  private sectionOrder: QuestSectionId[] = [];
  private selectedSection: QuestSectionId | null = null;
  /** Left preview (and middle row) highlight without expanding the accordion. */
  private highlightSection: QuestSectionId | null = null;
  private savedEl = el("span", "qe-saved");
  private savedAt: number | null = null;
  private saving = false;
  private saveFailed = false;
  private dirty = false;
  private saveTimer = 0;
  private tickTimer = 0;
  private unsubscribe: (() => void) | null = null;
  private onFocus = () => void this.refetch();
  private onKey = (e: KeyboardEvent) => this.handleKey(e);
  private adminBannerCollapse: CollapseBannerHandle | null = null;
  private saveHeartEl: HTMLButtonElement | null = null;
  private saveBusy = false;
  /** Title saves on blur / Enter / flush, not on every input (Korean IME-safe). */
  private titleSave = createDeferredCommit(() => this.scheduleSave());
  /** Modal title field kept across guide re-renders so typing keeps focus. */
  private modalTitleInput: HTMLInputElement | null = null;
  private spotLocPicker: SpotLocationPicker | null = null;
  private spotLocOpen = false;
  /** Ignore blur while moving the cover title between DOM parents during renderPage. */
  private titleBlurFromRepaint = false;
  private publishBtn: HTMLButtonElement | null = null;
  private saveChangesBtn: HTMLButtonElement | null = null;
  private draftSaveBtn: HTMLButtonElement | null = null;
  private collapseTimer = 0;
  /** JSON snapshot after the last successful load/save; drives Save button + leave prompts. */
  private persistedSnapshot = "";
  /** Per-checkpoint thumbnail display generation (cache bust when R2 key is unchanged). */
  private spotThumbBust = new Map<number, number>();
  /** Thumbnail URLs applied locally while a save/adoptServer swap is in flight. */
  private spotThumbPending = new Map<number, string>();
  /** Spot gallery URLs pending server echo (index → ordered urls). */
  private spotImagesPending = new Map<number, QuestSpotImage[]>();
  /** Spot thumbs mid-upload (dirty before pending URL is committed on the checkpoint). */
  private spotThumbUploadInFlight = new Set<number>();
  private editorDirtyAbort: AbortController | null = null;
  private spotPreviewEmbeds: SpotLocationEmbed[] = [];
  /** Spot picked on the quest map (checkpoint index); card shows under the map when set. */
  private mapSelectedSpotIndex: number | null = null;
  private mapSpotUiEl: HTMLElement | null = null;
  private mapSpotCardHost: HTMLElement | null = null;
  private mapSpotHintEl: HTMLElement | null = null;
  private mapSpotCloseBtn: HTMLButtonElement | null = null;
  private mapSpotPickListEl: HTMLUListElement | null = null;
  private mapSpotCardWrapEl: HTMLElement | null = null;
  private mapSpotCardEmbed: SpotLocationEmbed | null = null;

  constructor(opts: RouteTraceOptions) {
    this.opts = opts;
    this.q = opts.quest;
    if (!this.q.page_photos) this.q.page_photos = [];
    if (!this.q.page_blocks) this.q.page_blocks = [];
    this.mapBlock = readMapBlock(this.q.id) || Boolean(this.q.has_route) || this.q.quest_type === "spot_trace";
    this.sectionOrder = resolveEditorSectionOrder(this.q.page_order ?? null, this.orderContext());
  }

  private orderContext(): QuestOrderContext {
    return {
      quest_type: this.q.quest_type ?? null,
      checkpointCount: this.q.checkpoints.length,
      photoCount: this.q.page_photos?.length ?? 0,
      blockCount: this.q.page_blocks?.length ?? 0,
      checkpointKinds: this.q.checkpoints.map((cp) => cp.kind),
    };
  }

  private syncPageOrderField(): void {
    this.q.page_order = [...this.sectionOrder];
  }

  private selectSection(id: QuestSectionId | null): void {
    if (id === null && this.selectedSection != null && this.editorEl) {
      const prev = this.selectedSection;
      const li = this.editorEl.querySelector<HTMLElement>(`.qe-item[data-section-id="${CSS.escape(prev)}"]`);
      if (li?.classList.contains("is-expanded")) {
        li.classList.remove("is-expanded");
        const ms = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 220;
        window.clearTimeout(this.collapseTimer);
        this.collapseTimer = window.setTimeout(() => {
          this.collapseTimer = 0;
          if (this.selectedSection === prev) {
            this.selectedSection = null;
            this.render();
          }
        }, ms);
        return;
      }
    }
    window.clearTimeout(this.collapseTimer);
    this.selectedSection = id;
    if (id != null) this.highlightSection = id;
    const spotIdx = id != null ? parseSpotSectionId(id) : null;
    if (this.spotMapInteractive()) {
      this.mapSelectedSpotIndex = spotIdx;
      this.syncMapSpotPanel();
      this.syncMap();
    }
    this.render();
  }

  private highlightPreviewSection(id: QuestSectionId): void {
    const spotIdx = parseSpotSectionId(id);
    if (this.spotMapInteractive() && spotIdx != null) {
      const next = this.highlightSection === id ? null : id;
      this.highlightSection = next;
      this.mapSelectedSpotIndex = next != null ? spotIdx : null;
      this.syncEditableUi();
      this.syncMapSpotPanel();
      this.syncMap();
      if (this.highlightSection && this.editorEl) {
        scrollQuestEditorSectionIntoView(this.editorEl, this.highlightSection);
      }
      return;
    }
    this.highlightSection = this.highlightSection === id ? null : id;
    this.syncEditableUi();
    if (this.highlightSection && this.editorEl) {
      scrollQuestEditorSectionIntoView(this.editorEl, this.highlightSection);
    }
  }

  /** Refresh live preview and middle-column row labels without rebuilding expanded inputs. */
  private syncEditableUi(): void {
    if (!this.editable) return;
    this.renderEditablePreviewPage();
    if (this.editorEl) syncQuestEditorListChrome(this.editorColumnsHost(), this.editorEl);
    this.syncTitleDraftUi();
  }

  private get editable(): boolean {
    return this.opts.mode === "edit" && this.q.can_edit && this.opts.layout !== "inline";
  }

  /** An admin editing a published quest (changes go live on save). */
  private get adminLiveEdit(): boolean {
    return this.editable && this.q.status !== "draft";
  }

  mount(): void {
    this.syncPageOrderField();
    this.commitPersistedSnapshot();
    this.renderShell();
    if (this.editable) {
      this.bindEditorDirtyDomListeners();
      this.unsubscribe = onRouteSaved(this.q.id, () => void this.refetch(true));
      window.addEventListener("focus", this.onFocus);
      window.addEventListener("keydown", this.onKey);
      window.addEventListener("beforeunload", this.onBeforeUnload);
      this.tickTimer = window.setInterval(() => this.paintSaved(), 30_000);
      (window as unknown as { __mapusQuestEditor?: { debugSave(): unknown } }).__mapusQuestEditor = {
        debugSave: () => ({
          dirty: this.dirty,
          saving: this.saving,
          savedAt: this.savedAt,
          saveFailed: this.saveFailed,
          editable: this.editable,
          autosave: readQuestEditorAutosavePref(),
          thumb0: this.q.checkpoints[0]?.thumbnail_image ?? null,
          bust0: this.spotThumbBust.get(0) ?? 0,
          snapEqual: this.persistedSnapshot === questEditorDirtySnapshot(this.q, this.dirtyOpts()),
          isDirtyFn: questEditorIsDirty(this.persistedSnapshot, this.q, this.dirtyOpts()),
          btnDisabled: (document.querySelector("button.qe-bar-draft-save") as HTMLButtonElement | null)?.disabled ?? null,
        }),
      };
    }
  }

  /** The inline page's panel was reopened or resized. */
  relayout(): void {
    this.map?.relayout();
    this.coverMap?.relayout();
  }

  destroy(): void {
    this.editorDirtyAbort?.abort();
    this.editorDirtyAbort = null;
    this.unsubscribe?.();
    window.removeEventListener("focus", this.onFocus);
    window.removeEventListener("keydown", this.onKey);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    window.clearInterval(this.tickTimer);
    window.clearTimeout(this.saveTimer);
    window.clearTimeout(this.collapseTimer);
    this.adminBannerCollapse?.destroy();
    this.adminBannerCollapse = null;
    this.map?.destroy();
    this.map = null;
    this.dropCoverMap();
    this.coverInput?.remove();
    this.coverInput = null;
    this.dropSpotLocPicker();
    this.dropEditorRouteMap();
    this.dropSpotPreviewEmbeds();
  }

  private dropSpotPreviewEmbeds(): void {
    for (const embed of this.spotPreviewEmbeds) embed.destroy();
    this.spotPreviewEmbeds = [];
  }

  // ---------- layout ----------

  private homeExitHref(): string {
    const stored = readHomeBrowseReturn();
    if (stored?.returnToHome && stored.view) {
      return formatHomeBrowseUrl(stored.questId ?? this.q.id, stored.view);
    }
    return formatHomeBrowseUrl(this.q.id, null);
  }

  private renderShell(): void {
    if (this.opts.layout === "inline") {
      this.pageEl = el("article", "qp qp-inline");
      this.opts.root.replaceChildren(this.pageEl);
      this.renderPage();
      return;
    }
    const shell = el("div", this.editable ? "qe" : "qe qe-view");
    const draft = this.q.status === "draft";
    const adminBar = this.q.can_edit && !draft;
    const saveHeart = this.viewSaveHeart();
    this.publishBtn = null;
    this.saveChangesBtn = null;
    this.draftSaveBtn = null;
    shell.append(
      questBar({
        crumb: this.q.title.trim() || (draft ? t("qbar.newQuest") : t("qbar.draftQuest")),
        status: draft ? t("qbar.draft") : t("qbar.published"),
        saveHeart: saveHeart ?? undefined,
        previewHref: this.editable && draft ? questViewPath(this.q.id) : undefined,
        viewHref: adminBar ? questViewPath(this.q.id) : undefined,
        editHref: this.opts.mode === "view" && this.q.can_edit ? questEditPath(this.q.id) : undefined,
        exitHref: this.homeExitHref(),
        onExit: (e) => this.onExitClick(e),
        trailing: (bar) => this.appendQuestBarTrailing(bar, { draft, adminBar }),
      }),
    );
    if (this.editable) {
      const ws = questWorkspace();
      this.pageEl = ws.page;
      this.editorEl = ws.editor;
      this.paletteEl = ws.palette;
      shell.append(ws.root);
    } else {
      const ws = questViewWorkspace();
      this.pageEl = ws.page;
      this.guideEl = ws.guide;
      shell.append(ws.root);
    }
    mountLegalFooter(shell);
    this.opts.root.replaceChildren(shell);
    this.renderPage();
    if (this.editable) this.renderEditorColumns();
    else this.renderGuide();
    this.paintSaved();
    this.paintQuestBar();
  }

  private publishBarOpts(): QuestBarPublishOpts {
    const blockers: PublishBlockerKey[] = this.isSpotQuest()
      ? spotPublishBlockers({ title: this.q.title, checkpoints: this.q.checkpoints })
      : routePublishBlockers({
          title: this.q.title,
          has_route: Boolean(this.line()),
          route_settings_done: Boolean(this.q.route_settings_done),
        });
    return {
      disabled: blockers.length > 0,
      blockers,
      onPublish: () => void this.publish(),
    };
  }

  private appendQuestBarTrailing(
    bar: HTMLElement,
    _ctx: { draft: boolean; adminBar: boolean },
  ): void {
    const lockedPublished =
      !this.editable && this.opts.mode === "edit" && this.q.status !== "draft";
    if (lockedPublished) {
      const view = el("a", "qe-bar-btn qe-bar-btn-filled", t("rt.published.view"));
      view.href = questViewPath(this.q.id);
      bar.append(view);
      if (this.q.can_delete) {
        appendQuestBarOverflow(bar, {
          items: [{ label: t("quest.delete"), danger: true, onClick: () => void this.deleteQuest() }],
        });
      }
      return;
    }
    if (this.adminLiveEdit) {
      this.saveChangesBtn = appendQuestBarSaveChanges(bar, this.savedEl, {
        disabled: true,
        onSave: () => void this.flush(),
      });
      if (this.q.can_delete) {
        appendQuestBarOverflow(bar, {
          items: [{ label: t("quest.delete"), danger: true, onClick: () => void this.deleteQuest() }],
        });
      }
      return;
    }
    if (!this.editable || !_ctx.draft) return;
    appendQuestBarAutosaveToggle(bar, {
      onChange: (enabled) => {
        writeQuestEditorAutosavePref(enabled);
        if (enabled && this.dirty) this.scheduleSave(0);
        else window.clearTimeout(this.saveTimer);
      },
    });
    this.draftSaveBtn = appendQuestBarDraftSave(bar, this.savedEl, {
      disabled: true,
      onSave: () => void this.flushPending(),
    });
    this.publishBtn = appendQuestBarPublish(bar, this.publishBarOpts());
    appendQuestBarOverflow(bar, {
      items: [{ label: t("quest.deleteDraft"), danger: true, onClick: () => void this.deleteQuest() }],
    });
  }

  private paintQuestBar(): void {
    if (!this.publishBtn) return;
    paintQuestBarPublish(this.publishBtn, this.publishBarOpts());
  }

  private editorColumnsHost(): QuestEditorColumnsHost {
    const self = this;
    return {
      get quest() {
        return self.q;
      },
      orderContext: () => self.orderContext(),
      get sectionOrder() {
        return self.sectionOrder;
      },
      setSectionOrder(order) {
        self.sectionOrder = order;
        self.syncPageOrderField();
      },
      get selectedSection() {
        return self.selectedSection;
      },
      get highlightSection() {
        return self.highlightSection;
      },
      selectSection: (id) => self.selectSection(id),
      isSpotQuest: () => self.isSpotQuest(),
      get adminLiveEdit() {
        return self.adminLiveEdit;
      },
      get readyToPublish() {
        return self.isSpotQuest()
          ? spotTraceReady({ title: self.q.title, checkpoints: self.q.checkpoints })
          : routeTraceReady({
              title: self.q.title,
              has_route: Boolean(self.line()),
              route_settings_done: Boolean(self.q.route_settings_done),
            });
      },
      onScheduleSave: () => self.scheduleSave(),
      onEditorStateChanged: () => self.notifyEditorStateChanged(),
      onSyncSaveBar: () => self.notifyEditorStateChanged(),
      onSyncPreview: () => self.syncEditableUi(),
      onRenderAll: () => self.render(),
      onToast: (m) => self.opts.onToast(m),
      onPublish: () => void self.publish(),
      onDeleteDraft: () => void self.deleteQuest(),
      openRouteEditor: () => self.openRouteEditor(),
      addMapBlock: () => self.addMapBlock(),
      hasLine: () => Boolean(self.line()),
      get mapBlock() {
        return self.mapBlock;
      },
      settingsSummary: () => self.settingsSummary(),
      openCoverPicker: () => self.openCoverPicker(),
      removeCover: () => void self.removeCover(),
      line: () => self.line(),
      get placing() {
        return self.placing;
      },
      setPlacing: (k) => {
        self.placing = k;
        self.render();
      },
      placeRow: (k) => self.placeRow(k),
      setStartFixed: (f) => self.setStartFixed(f),
      setTimeLimit: (s) => self.setTimeLimit(s),
      suggestedLimit: () => self.suggestedLimit(),
      spotThumbForIndex: (i) => self.spotThumbDisplayUrl(i),
      uploadSpotThumb: (i, s, input) => self.uploadSpotThumb(i, s, input),
      spotImagesForIndex: (i) => self.spotImagesForIndex(i),
      uploadSpotImage: (i, input) => self.uploadSpotImage(i, input),
      removeSpotImage: (i, slot) => void self.removeSpotImage(i, slot),
      moveSpotImage: (i, from, dir) => self.moveSpotImage(i, from, dir),
      uploadPagePhoto: (i, p, input) => self.uploadPagePhoto(i, p, input),
      appendPageBlockDraft: (kind) => self.appendPageBlockDraft(kind),
      uploadGalleryItem: (bi, ii, input) => self.uploadGalleryItem(bi, ii, input),
      appendSpotDraft: (at) => self.appendSpotDraft(at),
      appendPhotoDraft: () => self.appendPhotoDraft(),
      dropSpotLocPicker: () => self.dropSpotLocPicker(),
      removeSpotAt: (index) => self.removeSpotAt(index),
      mountEditorRouteMap: (container) => self.mountEditorRouteMap(container),
    };
  }

  private renderEditorColumns(): void {
    this.dropEditorRouteMap();
    this.editorEl.replaceChildren();
    this.paletteEl.replaceChildren();
    if (this.adminLiveEdit) {
      const wasCollapsed = this.adminBannerCollapse?.isCollapsed() === true;
      this.adminBannerCollapse?.destroy();
      const liveBanner = createAdminPublishedBanner(this.q.title);
      const roots: (HTMLElement | Document)[] = [this.editorEl, this.pageEl, document];
      if (this.mapEl) roots.push(this.mapEl);
      this.editorEl.append(liveBanner);
      this.adminBannerCollapse = mountCollapseBanner(liveBanner, roots, {
        initiallyCollapsed: wasCollapsed,
      });
    }
    this.editorMount = {
      editorEl: this.editorEl,
      paletteEl: this.paletteEl,
      titleSave: this.titleSave,
      modalTitleInput: this.modalTitleInput,
      setModalTitleInput: (input) => {
        this.modalTitleInput = input;
      },
    };
    mountQuestEditorColumns(this.editorColumnsHost(), this.editorMount);
  }

  private viewSaveHeart(): HTMLButtonElement | null {
    if (this.editable || this.opts.layout === "inline") return null;
    if (!questSaveHeartEnabled(this.q.status)) return null;
    const btn = el("button", "qe-save-heart");
    btn.type = "button";
    this.paintSaveHeart(btn);
    btn.addEventListener("click", () => void this.toggleQuestSave());
    this.saveHeartEl = btn;
    return btn;
  }

  private paintSaveHeart(btn: HTMLButtonElement): void {
    paintQuestSaveHeart(btn, this.q.saved === true, this.saveBusy);
  }

  /** Keep the inline panel heart in sync when the quest page bar toggles save. */
  setQuestSaved(saved: boolean): void {
    if (this.q.saved === saved) return;
    this.q.saved = saved;
    if (this.saveHeartEl) this.paintSaveHeart(this.saveHeartEl);
  }

  private async toggleQuestSave(): Promise<void> {
    if (this.saveBusy) return;
    if (!this.opts.signedIn?.()) {
      this.opts.onRequireSignIn?.();
      return;
    }
    const next = !(this.q.saved === true);
    this.saveBusy = true;
    if (this.saveHeartEl) this.paintSaveHeart(this.saveHeartEl);
    try {
      if (next) await saveQuest(this.q.id);
      else await unsaveQuest(this.q.id);
      this.q.saved = next;
      this.opts.onSavedChange?.(next);
    } catch {
      this.opts.onToast(t("api.failed", { status: 0 }));
    } finally {
      this.saveBusy = false;
      if (this.saveHeartEl) this.paintSaveHeart(this.saveHeartEl);
    }
  }

  private paintSaved(): void {
    if (!this.editable) return;
    this.syncDirtyFromSnapshot();
    const live = this.adminLiveEdit;
    if (this.saving) {
      this.savedEl.textContent = live ? t("qbar.liveSaving") : t("qbar.saving");
      this.paintSaveChangesBtn();
      return;
    }
    if (this.saveFailed && this.savedAt == null) {
      this.savedEl.textContent = live ? t("qbar.liveSaveFailed") : t("qbar.saveFailed");
      this.paintSaveChangesBtn();
      return;
    }
    if (this.dirty) {
      this.savedEl.textContent = this.adminLiveEdit ? t("qbar.unsavedLive") : t("qbar.unsaved");
      this.paintSaveButtons();
      return;
    }
    if (this.savedAt == null) {
      this.savedEl.textContent = "";
      this.paintSaveChangesBtn();
      return;
    }
    const mins = Math.floor((Date.now() - this.savedAt) / 60_000);
    if (live) {
      this.savedEl.textContent =
        mins < 1 ? t("qbar.liveSavedJustNow") : t("qbar.liveSavedAgo", { count: mins });
    } else {
      this.savedEl.textContent =
        mins < 1 ? t("qbar.savedJustNow") : t("qbar.savedAgo", { count: mins });
    }
    this.paintSaveButtons();
  }

  private paintSaveButtons(): void {
    const enabled = this.dirty || this.saveFailed;
    if (this.saveChangesBtn) paintQuestBarSaveChanges(this.saveChangesBtn, !enabled);
    if (this.draftSaveBtn) paintQuestBarSaveChanges(this.draftSaveBtn, !enabled);
  }

  private paintSaveChangesBtn(): void {
    this.paintSaveButtons();
  }

  private hasUnsavedChanges(): boolean {
    return this.dirty || this.saving || (this.saveFailed && this.savedAt == null);
  }

  private onExitClick(e: MouseEvent): void {
    if (!this.hasUnsavedChanges()) return;
    if (!window.confirm(t("qbar.unsavedLeaveConfirm"))) e.preventDefault();
  }

  private onBeforeUnload = (e: BeforeUnloadEvent): void => {
    if (!this.editable || !this.hasUnsavedChanges()) return;
    e.preventDefault();
    e.returnValue = "";
  };

  // ---------- live page ----------

  private line(): LngLat[] | null {
    const coords = this.q.route?.coordinates;
    return coords && coords.length >= 2 ? coords : null;
  }

  private isSpotQuest(): boolean {
    return this.q.quest_type === "spot_trace";
  }

  /** Route polyline only; spot quests never draw connecting lines. */
  private pathLine(): LngLat[] | null {
    if (this.isSpotQuest()) return null;
    const route = this.line();
    if (route) return route;
    return null;
  }

  private pageSpots(): QuestSpot[] {
    if (this.isSpotQuest()) return this.q.checkpoints;
    return shownSpots(this.q);
  }

  private pin(kind: PinKind): QuestSpot | undefined {
    return this.q.checkpoints.find((s) => s.kind === kind);
  }

  /** Start/Finish as the page and map show them: only when the start is fixed. */
  private shownPin(kind: PinKind): QuestSpot | undefined {
    return this.q.start_fixed ? this.pin(kind) : undefined;
  }

  private settingsVisible(): boolean {
    return Boolean(this.q.route_settings_done) || this.modal?.kind === "settings";
  }

  private inlinePublishedHref(): string | null {
    if (this.opts.layout !== "inline" || this.editable) return null;
    return publishedQuestViewHref(this.q);
  }

  private questPageLinkLabel(title: string): string {
    return t("home.openQuestPage", { title });
  }

  private linkTitleHeading(text: string): HTMLElement {
    const href = this.inlinePublishedHref();
    if (!href) return el("h1", "qp-title", text);
    const link = el("a", "qp-title-link");
    link.href = href;
    link.setAttribute("aria-label", this.questPageLinkLabel(text));
    link.append(el("h1", "qp-title", text));
    return link;
  }

  private previewSpotForIndex(index: number): QuestSpot {
    const spot = this.q.checkpoints[index];
    if (!spot) {
      throw new Error(`missing spot ${index}`);
    }
    return {
      ...spot,
      thumbnail_image: this.spotThumbMarkerUrl(index),
      images: this.spotImagesForIndex(index),
    };
  }

  private previewWrap(id: QuestSectionId, node: HTMLElement | null): HTMLElement | null {
    if (!node) return null;
    const wrap = el("div", `qe-preview-section${this.highlightSection === id ? " is-selected" : ""}`);
    wrap.dataset.sectionId = id;
    wrap.append(node);
    const select = () => this.highlightPreviewSection(id);
    wrap.tabIndex = 0;
    wrap.setAttribute("role", "button");
    wrap.addEventListener("click", select);
    wrap.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        select();
      }
    });
    return wrap;
  }

  private renderEditablePreviewPage(): void {
    this.dropSpotPreviewEmbeds();
    const page = this.pageEl;
    page.replaceChildren();
    this.appendOrderedSections(page, true);
    this.syncMap();
  }

  private renderPublicOrderedPage(): void {
    this.dropSpotPreviewEmbeds();
    const page = this.pageEl;
    page.replaceChildren();
    this.appendOrderedSections(page, false);
    const line = this.line();
    if ((line || this.isSpotQuest()) && !this.editable) page.append(mountRouteQuestExplainer(this.q));
    const done = this.doneEl();
    const inline = this.opts.layout === "inline";
    if (!inline) {
      const ad = this.adEl();
      if (ad) page.append(ad);
    }
    if (done) page.append(done);
    if (inline) {
      const ad = this.adEl();
      if (ad) page.append(ad);
    }
    if (!this.q.mine && this.q.status === "active") {
      page.append(button(t("report.button"), "qp-report", () => this.openReport((reason) => reportQuest(this.q.id, reason))));
    }
    if (this.opts.layout !== "inline") {
      applyQuestPageHead({
        title: this.q.title,
        description: questMetaDescriptionFromQuest(this.q),
      });
    }
    this.syncMap();
  }

  private appendOrderedSections(page: HTMLElement, selectable: boolean): void {
    const path = this.pathLine();
    const line = this.line();
    const order = selectable ? this.sectionOrder : resolveEditorSectionOrder(this.q.page_order, this.orderContext());
    for (const id of order) {
      const spotIndex = parseSpotSectionId(id);
      if (spotIndex != null) {
        const spot = this.q.checkpoints[spotIndex];
        if (!spot) continue;
        if (!this.isSpotQuest() && isRouteEndpointCheckpoint(spot.kind)) continue;
        const previewSpot = this.previewSpotForIndex(spotIndex);
        if (this.spotMapInteractive()) {
          continue;
        }
        if (selectable) {
          const section = el("section", "qp-spots qp-spots-single");
          section.append(
            buildSpotTracePreviewCard(previewSpot, {
              onEmbed: (embed) => this.spotPreviewEmbeds.push(embed),
            }),
          );
          this.appendSectionNode(page, id, section, selectable);
        } else if (this.isSpotQuest()) {
          this.appendSectionNode(
            page,
            id,
            spotTracePreviewSection([spot], {
              onEmbed: (embed) => this.spotPreviewEmbeds.push(embed),
            }),
            selectable,
          );
        }
        continue;
      }
      const photoIndex = parsePhotoSectionId(id);
      if (photoIndex != null) {
        const photo = this.q.page_photos?.[photoIndex];
        if (!photo?.image) continue;
        this.appendSectionNode(page, id, this.pagePhotoEl(photo), selectable);
        continue;
      }
      const blockIndex = parseBlockSectionId(id);
      if (blockIndex != null) {
        const block = this.q.page_blocks?.[blockIndex];
        if (!block) continue;
        const node = renderPageBlock(block, { interactiveQuiz: !selectable && !this.editable });
        if (!node) continue;
        this.appendSectionNode(page, id, node, selectable);
        continue;
      }
      if (id === QUEST_SECTION_TITLE) {
        const title = this.q.title.trim();
        const head = el("header", `qp-head${this.q.cover_image || this.coverPreview ? " is-under-cover" : ""}`);
        if (title && !(selectable && (this.q.cover_image || this.coverPreview))) {
          head.append(this.linkTitleHeading(title));
        }
        const meta: string[] = [];
        if (!this.isSpotQuest() && line) {
          meta.push(formatDistance(this.q.route?.length_m ?? 0));
        }
        if (meta.length) head.append(el("p", "qp-meta", meta.join(" · ")));
        const cover = this.coverEl(path, selectable);
        if (selectable) {
          const block = el("div", "qp-title-block");
          if (cover) block.append(cover);
          if (head.childElementCount) block.append(head);
          if (block.childElementCount) this.appendSectionNode(page, id, block, selectable);
        } else {
          if (cover) page.append(cover);
          if (head.childElementCount) page.append(head);
        }
        continue;
      }
      if (id === QUEST_SECTION_INTRO) {
        const prose = this.q.description?.trim();
        if (!prose) continue;
        const block = el("header", "qp-head");
        block.append(el("p", "qp-desc", prose));
        this.appendSectionNode(page, id, block, selectable);
        continue;
      }
      if (id === QUEST_SECTION_MAP) {
        if (this.isSpotQuest() && (this.mapBlock || path || this.q.checkpoints.length)) {
          this.appendSectionNode(page, id, this.mapBlockEl(selectable), selectable);
        }
        continue;
      }
      if (id === QUEST_SECTION_ROUTE) {
        if (this.isSpotQuest()) continue;
        this.appendSectionNode(page, id, this.routeArtPreviewBlock(selectable), selectable);
        continue;
      }
      if (!this.isSpotQuest() && (id === QUEST_SECTION_SETTINGS || id === QUEST_SECTION_SPOTS)) {
        continue;
      }
      if (id === QUEST_SECTION_SETTINGS) {
        if (!this.settingsVisible()) continue;
        this.appendSectionNode(page, id, el("p", "qp-strip", this.settingsSummary()), selectable);
        continue;
      }
      if (id === QUEST_SECTION_SPOTS && line && this.isSpotQuest()) {
        const spots = this.pageSpots();
        if (spots.length) this.appendSectionNode(page, id, this.spotsEl(line, spots), selectable);
        continue;
      }
      if (id === QUEST_SECTION_STORY && this.q.story?.trim()) {
        this.appendSectionNode(page, id, textSection(t("rt.page.story"), this.q.story), selectable);
        continue;
      }
      if (id === QUEST_SECTION_MANNERS && this.q.manners?.trim()) {
        this.appendSectionNode(page, id, textSection(t("rt.page.manners"), this.q.manners), selectable);
      }
    }
  }

  private appendSectionNode(page: HTMLElement, id: QuestSectionId, node: HTMLElement, selectable: boolean): void {
    if (selectable) {
      const wrapped = this.previewWrap(id, node);
      if (wrapped) page.append(wrapped);
    } else {
      page.append(node);
    }
  }

  private pagePhotoEl(photo: QuestPagePhoto): HTMLElement {
    const fig = el("figure", "qp-page-photo");
    const img = el("img", "") as HTMLImageElement;
    img.src = photo.image!;
    img.alt = photo.caption?.trim() ?? "";
    img.loading = "lazy";
    fig.append(img);
    const cap = photo.caption?.trim();
    if (cap) fig.append(el("figcaption", "", cap));
    return fig;
  }

  private renderPage(): void {
    if (this.editable) {
      this.renderEditablePreviewPage();
      return;
    }
    if (this.q.page_order?.length) {
      this.renderPublicOrderedPage();
      return;
    }
    const page = this.pageEl;
    const caret = this.titleCaret();
    if (caret != null) this.titleBlurFromRepaint = true;
    this.dropSpotPreviewEmbeds();
    page.replaceChildren();
    const line = this.line();
    const path = this.pathLine();
    const spots = this.pageSpots();
    const title = this.q.title.trim();
    const description = this.q.description?.trim();
    const autoSummary =
      !this.editable && !description && (line || this.isSpotQuest()) ? questAutoSummaryText(this.q) : null;
    const cover = this.coverEl(path);
    if (cover) page.append(cover);
    if (caret != null) {
      this.restoreTitleCaret(caret);
      this.titleBlurFromRepaint = false;
    }
    const parts: string[] = [];
    if (line && (title || cover)) {
      parts.push(
        formatDistance(this.q.route?.length_m ?? 0),
        t("rt.page.walk", { time: `~${duration(walkMinutes(this.q.route?.length_m ?? 0))}` }),
      );
      if (spots.length) parts.push(t("rt.page.spotCount", { count: spots.length }));
    } else if (this.isSpotQuest() && spots.length && (title || cover)) {
      parts.push(t("rt.page.spotCount", { count: spots.length }));
      const totalMin = sumEstMinutes(spots);
      if (totalMin > 0) parts.push(t("rt.page.walk", { time: `~${duration(totalMin)}` }));
      const walkM = spotPathDistanceM(spots);
      if (walkM > 0) parts.push(formatDistance(Math.round(walkM)));
    }
    if (cover) parts.push(...this.byline());
    const prose = description || autoSummary;
    if ((title && !cover) || prose || parts.length) {
      const head = el("header", `qp-head${cover ? " is-under-cover" : ""}`);
      if (title && !cover) head.append(this.linkTitleHeading(title));
      if (parts.length) head.append(el("p", "qp-meta", parts.join(" · ")));
      if (prose) head.append(el("p", "qp-desc", prose));
      page.append(head);
    }
    if (this.mapBlock || path || (this.isSpotQuest() && spots.length)) page.append(this.mapBlockEl());
    const area = line ? routePlaceLabel(this.q.route_place, locale()) : null;
    if (area) {
      const row = el("p", "qp-area", area);
      row.title = t("rt.page.placeRegion");
      page.append(row);
    }
    if ((line || this.isSpotQuest()) && !this.editable) page.append(mountRouteQuestExplainer(this.q));
    if ((line || this.isSpotQuest()) && this.settingsVisible()) page.append(el("p", "qp-strip", this.settingsSummary()));
    if (line && spots.length) page.append(this.spotsEl(line, spots));
    else if (this.isSpotQuest() && (this.editable || spots.length) && !this.spotMapInteractive()) {
      page.append(this.spotTraceSpotsSection(spots));
    }
    if (this.q.story?.trim()) page.append(textSection(t("rt.page.story"), this.q.story));
    if (this.q.manners?.trim()) page.append(textSection(t("rt.page.manners"), this.q.manners));
    const done = this.doneEl();
    const inline = this.opts.layout === "inline";
    if (!inline) {
      const ad = this.adEl();
      if (ad) page.append(ad);
    }
    if (done) page.append(done);
    if (inline) {
      const ad = this.adEl();
      if (ad) page.append(ad);
    }
    if (!this.editable && !this.q.mine && this.q.status === "active") {
      page.append(button(t("report.button"), "qp-report", () => this.openReport((reason) => reportQuest(this.q.id, reason))));
    }
    if (!this.editable && this.opts.layout !== "inline") {
      applyQuestPageHead({
        title: this.q.title,
        description: questMetaDescriptionFromQuest(this.q),
      });
    }
    this.syncMap();
  }

  // ---------- ads ----------

  private adEl(): HTMLElement | null {
    if (this.editable) return null;
    if (this.q.status !== "active" && this.q.status !== "hidden") return null;
    const placement = this.opts.layout === "inline" ? "home" : "quest";
    return mountAdSlot(placement, t("ads.label"));
  }

  // ---------- completed by ----------

  private doneEl(): HTMLElement | null {
    if (this.editable || (this.q.status !== "active" && this.q.status !== "hidden")) return null;
    if (!this.done) void this.loadDone();
    const done = this.done;
    if (!done || done.failed || (done.loading && !done.items.length)) return null;
    const viewer = done.viewer;
    const section = el("section", "qp-done");
    const h = el("h2", "qp-h2", t("done.title"));
    if (done.total) h.append(el("span", "qp-done-count", String(done.total)));
    section.append(h, el("p", "qp-done-hint", t("done.hint")));
    const form = this.lineFormEl(viewer);
    if (form) section.append(form);
    if (!done.items.length) {
      section.append(el("p", "qp-done-empty", t("done.empty")));
      return section;
    }
    const list = el("ul", "qp-done-list");
    for (const item of done.items) list.append(this.doneRowEl(item, viewer));
    section.append(list);
    if (done.items.length < done.total) {
      const more = button(t("done.more"), "qp-done-more", () => void this.loadDone());
      more.disabled = done.loading;
      section.append(more);
    }
    return section;
  }

  private doneRowEl(item: QuestCompletion, viewer: CompletionViewer | null): HTMLElement {
    const row = el("li", "qp-done-row");
    const name = item.name ?? t("done.anon");
    if (item.picture) {
      const img = el("img", "qp-done-avatar");
      img.src = item.picture;
      img.alt = "";
      img.loading = "lazy";
      img.referrerPolicy = "no-referrer";
      img.addEventListener("error", () => img.replaceWith(initial(name)));
      row.append(img);
    } else {
      row.append(initial(name));
    }
    const body = el("div", "qp-done-body");
    const head = el("p", "qp-done-head");
    head.append(el("span", "qp-done-name", name), el("span", "qp-done-date", ` · ${formatDate(item.completed_at)}`));
    body.append(head);
    const line = item.line;
    const editingMine = item.mine && this.lineForm?.editing;
    if (line && !editingMine) {
      const text = el("p", "qp-done-line");
      text.textContent = line.body;
      const acts = el("span", "qp-done-acts");
      if (item.mine && viewer?.can_write) {
        acts.append(
          button(t("done.edit"), "qp-done-act", () => this.editLine(line.body)),
          button(t("done.delete"), "qp-done-act", () => void this.dropLine()),
        );
      } else if (item.mine) {
        acts.append(button(t("done.delete"), "qp-done-act", () => void this.dropLine()));
      } else if (viewer) {
        acts.append(
          button(t("done.report"), "qp-done-act", () =>
            this.openReport((reason) => reportQuestLine(this.q.id, line.id, reason)),
          ),
        );
        if (viewer.admin) acts.append(button(t("done.remove"), "qp-done-act is-danger", () => void this.adminDropLine(line.id)));
      }
      if (acts.childElementCount) text.append(acts);
      body.append(text);
    }
    row.append(body);
    return row;
  }

  /** The inline input for a finisher with no line yet (or editing theirs). */
  private lineFormEl(viewer: CompletionViewer | null): HTMLElement | null {
    if (!viewer?.can_write) return null;
    if (viewer.line && !this.lineForm?.editing) return null;
    const { form, input, error, editing } = this.ensureLineForm();
    const row = el("div", "qp-done-form-row");
    const post = el("button", "qp-done-post", t(editing ? "done.save" : "done.post"));
    post.type = "submit";
    post.disabled = this.lineBusy;
    row.append(input, post);
    if (editing) {
      row.append(
        button(t("done.cancel"), "qp-done-act", () => {
          this.lineForm = null;
          this.renderPage();
        }),
      );
    }
    form.replaceChildren(row, error);
    return form;
  }

  private ensureLineForm(): NonNullable<RouteTraceEditor["lineForm"]> {
    if (this.lineForm) return this.lineForm;
    const form = el("form", "qp-done-form");
    const input = el("input", "qp-done-input");
    input.type = "text";
    input.maxLength = QUEST_LINE_MAX;
    input.placeholder = t("done.write");
    input.setAttribute("aria-label", t("done.write"));
    input.enterKeyHint = "send";
    const error = el("p", "qp-done-err");
    error.hidden = true;
    input.addEventListener("input", () => {
      error.hidden = true;
    });
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      void this.postLine();
    });
    this.lineForm = { form, input, error, editing: false };
    return this.lineForm;
  }

  private editLine(body: string): void {
    this.lineForm = null;
    const form = this.ensureLineForm();
    form.editing = true;
    form.input.value = body;
    this.renderPage();
    form.input.focus();
    form.input.setSelectionRange(body.length, body.length);
  }

  private async postLine(): Promise<void> {
    const form = this.lineForm;
    if (!form || this.lineBusy) return;
    const text = normalizeQuestLine(form.input.value);
    const problem = questLineProblem(text);
    if (problem) {
      form.error.textContent = t(`done.err.${problem}`, { max: QUEST_LINE_MAX });
      form.error.hidden = false;
      return;
    }
    this.lineBusy = true;
    this.renderPage();
    try {
      const line = await saveQuestLine(this.q.id, text);
      this.lineForm = null;
      this.setMyLine(line);
    } catch (err) {
      form.error.textContent = err instanceof Error ? err.message : t("api.failed", { status: 0 });
      form.error.hidden = false;
    } finally {
      this.lineBusy = false;
      this.renderPage();
    }
  }

  private async dropLine(): Promise<void> {
    try {
      await deleteQuestLine(this.q.id);
      this.lineForm = null;
      this.setMyLine(null);
      this.renderPage();
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("api.failed", { status: 0 }));
    }
  }

  private async adminDropLine(lineId: string): Promise<void> {
    try {
      await removeQuestLine(lineId);
      if (this.done) {
        this.done = {
          ...this.done,
          items: this.done.items.map((i) => (i.line?.id === lineId ? { ...i, line: null } : i)),
        };
      }
      this.opts.onToast(t("done.removed"));
      this.renderPage();
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("api.failed", { status: 0 }));
    }
  }

  private setMyLine(line: { id: string; body: string } | null): void {
    const done = this.done;
    if (!done?.viewer) return;
    this.done = {
      ...done,
      viewer: { ...done.viewer, line },
      items: done.items.map((i) => (i.mine ? { ...i, line } : i)),
    };
  }

  /** First page: DONE_FIRST people; each Show more adds DONE_PAGE until everyone is listed. */
  private async loadDone(): Promise<void> {
    const prev = this.done ?? { total: 0, items: [], viewer: null, loading: false, failed: false };
    if (prev.loading) return;
    const first = this.done == null;
    this.done = { ...prev, loading: true };
    if (!first) this.renderPage();
    const id = this.q.id;
    try {
      const page = await listCompletions(id, prev.items.length, first ? DONE_FIRST : DONE_PAGE);
      if (this.q.id !== id) return;
      this.done = {
        total: page.total,
        items: [...prev.items, ...page.completions],
        viewer: first ? page.viewer : prev.viewer,
        loading: false,
        failed: false,
      };
    } catch {
      this.done = { ...prev, loading: false, failed: first };
    }
    this.renderPage();
  }

  // ---------- cover ----------

  /**
   * The 16:9 cover slot: the photo, or the route map at its canvas bearing when there is
   * none. While editing it takes a click or a dropped image.
   */
  private coverEl(path: LngLat[] | null, readOnlyPreview = false): HTMLElement | null {
    const image = this.coverPreview ?? this.q.cover_image ?? null;
    const editable = this.editable && !readOnlyPreview;
    if (!image && !path && !editable) {
      this.dropCoverMap();
      return null;
    }
    const wrap = el("div", "qp-cover-wrap");
    const slot = el("section", `qp-cover${editable ? " is-editable" : ""}`);
    if (image) {
      this.dropCoverMap();
      if (this.coverImg?.getAttribute("src") !== image) {
        this.coverImg = el("img", "qp-cover-img");
        this.coverImg.src = image;
        this.coverImg.alt = "";
        this.coverImg.decoding = "async";
      }
      slot.append(this.coverImg);
    } else if (path) {
      slot.append(this.coverMapBlock(path));
    } else {
      this.dropCoverMap();
      const empty = el("div", "qp-cover-empty");
      empty.append(el("span", "qp-cover-empty-title", t("cover.add")), el("span", "qp-cover-empty-sub", t("cover.addSub")));
      slot.append(empty);
    }
    const title = this.coverTitleEl(readOnlyPreview);
    if (title) {
      const text = el("div", "qp-cover-text");
      text.append(title);
      slot.append(el("span", "qp-cover-scrim"), text);
    }
    if (this.coverPreview) slot.append(el("span", "qp-cover-busy", t("cover.uploading")));
    const pageHref = this.inlinePublishedHref();
    if (pageHref) {
      const link = el("a", "qp-cover-link");
      link.href = pageHref;
      const labelTitle = this.q.title.trim() || t("qbar.draftQuest");
      link.setAttribute("aria-label", this.questPageLinkLabel(labelTitle));
      while (slot.firstChild) link.append(slot.firstChild);
      slot.append(link);
    }
    if (editable) {
      if (!this.coverPreview && (image || path)) {
        const actions = el("div", "qp-cover-actions");
        actions.append(button(image ? t("cover.replace") : t("cover.add"), "qp-cover-btn", () => this.openCoverPicker()));
        if (image) actions.append(button(t("cover.remove"), "qp-cover-btn", () => void this.removeCover()));
        slot.append(actions);
      }
      slot.tabIndex = 0;
      slot.setAttribute("role", "button");
      slot.setAttribute("aria-label", image ? t("cover.replace") : t("cover.add"));
      slot.addEventListener("click", (e) => {
        if ((e.target as HTMLElement).closest("a, button, .qp-cover-title")) return;
        this.openCoverPicker();
      });
      slot.addEventListener("keydown", (e) => {
        if (e.target !== slot || (e.key !== "Enter" && e.key !== " ")) return;
        e.preventDefault();
        this.openCoverPicker();
      });
      slot.addEventListener("dragover", (e) => {
        if (!e.dataTransfer?.types.includes("Files")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        slot.classList.add("is-drop");
      });
      slot.addEventListener("dragleave", () => slot.classList.remove("is-drop"));
      slot.addEventListener("drop", (e) => {
        e.preventDefault();
        slot.classList.remove("is-drop");
        const file = e.dataTransfer?.files[0];
        if (file) void this.pickCover(file);
      });
    }
    wrap.append(slot);
    if (editable) wrap.append(el("p", "qp-cover-note", t("cover.notice")));
    return wrap;
  }

  /** The title over the cover's bottom scrim: plain in views, typed in place while editing. */
  private coverTitleEl(readOnlyPreview = false): HTMLElement | null {
    const title = this.q.title.trim();
    if (!this.editable || readOnlyPreview) {
      this.coverTitle = null;
      return title ? el("h1", "qp-cover-title", title) : null;
    }
    let node = this.coverTitle;
    if (!node) {
      node = el("h1", "qp-cover-title is-editable");
      node.contentEditable = "plaintext-only";
      if (node.contentEditable !== "plaintext-only") node.contentEditable = "true";
      node.spellcheck = false;
      node.setAttribute("role", "textbox");
      node.setAttribute("aria-label", t("cover.titlePlaceholder"));
      node.dataset.placeholder = t("cover.titlePlaceholder");
      this.titleSave.bindComposition(node);
      node.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          node!.blur();
        }
      });
      node.addEventListener("input", () => {
        let text = (node!.textContent ?? "").replace(/\s*\n\s*/g, " ");
        if (text.length > MAX_TITLE) {
          text = text.slice(0, MAX_TITLE);
          node!.textContent = text;
          this.restoreTitleCaret(text.length);
        }
        if (!text.trim() && node!.textContent) node!.textContent = "";
        this.q.title = text;
        this.titleSave.noteEdit();
        this.syncTitleDraftUi();
      });
      node.addEventListener("blur", () => {
        if (this.titleBlurFromRepaint) return;
        const trimmed = this.q.title.trim();
        if (node!.textContent !== trimmed) node!.textContent = trimmed;
        this.titleSave.commit();
        this.renderPage();
        this.renderGuide();
      });
      this.coverTitle = node;
    }
    if (document.activeElement !== node && node.textContent !== this.q.title) node.textContent = this.q.title;
    return node;
  }

  /** The creator and the publish date (created date for drafts), for the meta line under the cover. */
  private byline(): string[] {
    return [this.q.author?.trim() ?? "", formatDate(coverDate(this.q))].filter(Boolean);
  }

  /** Caret offset in the cover title while it has focus, else null. */
  private titleCaret(): number | null {
    const node = this.coverTitle;
    if (!node || document.activeElement !== node) return null;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return node.textContent?.length ?? 0;
    const range = sel.getRangeAt(0).cloneRange();
    range.selectNodeContents(node);
    range.setEnd(sel.getRangeAt(0).endContainer, sel.getRangeAt(0).endOffset);
    return range.toString().length;
  }

  /** Live updates while typing a title without rebuilding the page (keeps focus). */
  private syncTitleDraftUi(): void {
    const trimmed = this.q.title.trim();
    const draft = this.q.status === "draft";
    const crumbEl = this.opts.root.querySelector(".qe-crumb");
    if (crumbEl) {
      const status = crumbEl.querySelector(".qe-crumb-status");
      const label = trimmed || (draft ? t("qbar.newQuest") : t("qbar.draftQuest"));
      crumbEl.childNodes.forEach((n) => {
        if (n !== status) n.remove();
      });
      crumbEl.insertBefore(document.createTextNode(label), status);
    }
    const chunk = this.editorEl?.querySelector(`[data-section-id="${QUEST_SECTION_TITLE}"] .qe-item-summary, [data-section-id="${QUEST_SECTION_TITLE}"] .qe-item-title`);
    if (!chunk) {
      const legacy = this.guideEl?.querySelector('[data-chunk="title"]');
      const summary = legacy?.querySelector(".rt-chunk-summary, .rt-chunk-desc");
      if (summary) {
        summary.className = trimmed ? "rt-chunk-summary" : "rt-chunk-desc";
        summary.textContent = trimmed ? `“${trimmed}”` : this.chunkDesc("title");
      }
    }
    const listTitle = this.editorEl?.querySelector(
      `.qe-item[data-section-id="${QUEST_SECTION_TITLE}"] .qe-item-summary`,
    );
    if (listTitle) listTitle.textContent = trimmed || t("rt.title.desc");
    const bannerTitle = this.editorEl?.querySelector(".editor-banner-quest-title") ??
      this.guideEl?.querySelector(".editor-banner-quest-title");
    if (bannerTitle) bannerTitle.textContent = trimmed || t("qbar.newQuest");
    const next = this.guideEl.querySelector<HTMLButtonElement>(".rt-modal .rt-next");
    if (next && this.modal?.kind === "title" && this.modal.step === 0) next.disabled = !trimmed;
    const cover = this.coverTitle;
    if (cover && document.activeElement !== cover && cover.textContent !== this.q.title) {
      cover.textContent = this.q.title;
    }
  }

  private restoreTitleCaret(offset: number): void {
    const node = this.coverTitle;
    if (!node || !node.isConnected) return;
    if (document.activeElement !== node) node.focus({ preventScroll: true });
    const text = node.firstChild;
    const range = document.createRange();
    if (text) range.setStart(text, Math.min(offset, text.textContent?.length ?? 0));
    else range.setStart(node, 0);
    range.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  private coverMapBlock(line: LngLat[]): HTMLElement {
    if (!this.coverMapEl || !this.coverMap) {
      this.coverMapEl = el("div", "qp-cover-map");
      this.coverMapEl.setAttribute("role", "img");
      this.coverMapEl.setAttribute("aria-label", t("cover.mapAlt"));
      this.coverMap = new RouteMap(this.coverMapEl, [this.q.center_lng, this.q.center_lat], { still: true });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.coverMap?.relayout());
      });
    }
    this.coverMap.setRoute(line, this.q.route?.canvas ?? null, this.q.route?.paint ?? null, this.q.route?.fill ?? null);
    return this.coverMapEl;
  }

  private dropCoverMap(): void {
    this.coverMap?.destroy();
    this.coverMap = null;
    this.coverMapEl = null;
  }

  private openCoverPicker(): void {
    if (!this.editable || this.coverPreview) return;
    if (!this.coverInput) {
      const input = el("input");
      input.type = "file";
      input.accept = "image/*";
      input.hidden = true;
      input.addEventListener("change", () => {
        const file = input.files?.[0];
        input.value = "";
        if (file) void this.pickCover(file);
      });
      this.coverInput = input;
      document.body.append(input);
    }
    this.coverInput.click();
  }

  private async pickCover(file: File): Promise<void> {
    if (!this.editable || this.coverPreview) return;
    let blob: Blob | null;
    try {
      blob = await cropCover(file);
    } catch (err) {
      this.opts.onToast(err instanceof CoverPickError ? err.message : t("cover.err.read"));
      return;
    }
    if (!blob) return;
    this.coverPreview = URL.createObjectURL(blob);
    this.renderPage();
    try {
      this.q.cover_image = await uploadQuestCover(this.q.id, blob);
      this.opts.onToast(t("cover.saved"));
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("qbar.saveFailed"));
    } finally {
      URL.revokeObjectURL(this.coverPreview);
      this.coverPreview = null;
      this.render();
      this.notifyEditorStateChanged();
    }
  }

  private async removeCover(): Promise<void> {
    if (!this.q.cover_image || !window.confirm(t("cover.removeConfirm"))) return;
    try {
      await removeQuestCover(this.q.id);
      this.q.cover_image = null;
      this.opts.onToast(t("cover.removed"));
      if (this.editable) this.render();
      else this.renderPage();
      this.notifyEditorStateChanged();
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("qbar.saveFailed"));
    }
  }

  private openReport(send: (reason: string) => Promise<void>): void {
    const overlay = el("div", "cover-crop");
    const box = el("div", "cover-crop-box qp-report-box");
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");
    box.setAttribute("aria-label", t("report.title"));
    const close = () => {
      window.removeEventListener("keydown", onKey, true);
      overlay.remove();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      close();
    };
    const list = el("div", "rt-choices");
    for (const reason of REPORT_REASONS) {
      list.append(
        button(t(`report.reason.${reason}`), "rt-choice", () => {
          close();
          void this.sendReport(() => send(reason));
        }),
      );
    }
    const foot = el("div", "cover-crop-foot");
    foot.append(button(t("report.cancel"), "rt-back", close));
    box.append(el("h2", "rt-q", t("report.title")), list, foot);
    overlay.append(box);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close();
    });
    window.addEventListener("keydown", onKey, true);
    document.body.append(overlay);
    box.querySelector<HTMLButtonElement>(".rt-choice")?.focus();
  }

  private async sendReport(send: () => Promise<void>): Promise<void> {
    try {
      await send();
      this.opts.onToast(t("report.sent"));
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("api.failed", { status: 0 }));
    }
  }

  private routeArtPreviewBlock(readOnlyPreview: boolean): HTMLElement {
    const block = el("div", "qe-route-preview-block");
    if (this.mapBlock || this.pathLine()) {
      block.append(this.mapBlockEl(readOnlyPreview, { areaLabel: t("rt.route") }));
    }
    if (this.settingsVisible()) {
      block.append(el("p", "qp-strip", this.settingsSummary()));
    }
    return block;
  }

  private mapBlockEl(
    readOnlyPreview = false,
    opts: { areaLabel?: string } = {},
  ): HTMLElement {
    const block = el("section", "qp-map-block");
    block.setAttribute("aria-label", opts.areaLabel ?? t("rt.page.area"));
    const interactiveMap = this.spotMapInteractive();
    if (!this.mapEl) {
      this.mapEl = el("div", "qp-map");
      this.map = new RouteMap(this.mapEl, [this.q.center_lng, this.q.center_lat], {
        onPlace: (kind, point) => this.placePin(kind, point),
        onMiss: () => this.opts.onToast(t("rt.set.miss")),
        onSpotTap: undefined,
        onSpotMarkerSelect: (index) => this.selectMapSpot(index),
        spotTraceMode: this.isSpotQuest(),
        resetViewLabel: this.isSpotQuest() ? "rt.map.resetSpots" : undefined,
        fitExtraTop: () => {
          const block = this.mapEl?.parentElement;
          if (!block) return [];
          return [...block.querySelectorAll<HTMLElement>(".qp-map-tip")];
        },
      });
    }
    const label = el("span", "qp-map-label", opts.areaLabel ?? t("rt.page.area"));
    block.append(label, this.mapEl);
    if (interactiveMap) {
      if (!this.mapSpotUiEl) {
        this.mapSpotUiEl = el("div", "qp-spot-map-ui");
        this.mapSpotHintEl = el("p", "qp-spot-map-hint", t("rt.spot.mapPickHint"));
        this.mapSpotCardHost = el("div", "qp-spot-map-card-host");
        this.mapSpotCloseBtn = mapControlButton("qp-spot-map-close", t("rt.spot.mapClose"), CLOSE_ICON_SVG, () =>
          this.selectMapSpot(null),
        );
        const cardWrap = el("div", "qp-spot-map-card-wrap is-hidden");
        cardWrap.append(this.mapSpotCloseBtn, this.mapSpotCardHost);
        this.mapSpotCardWrapEl = cardWrap;
        this.mapSpotPickListEl = el("ul", "qp-spot-map-picklist");
        this.mapSpotPickListEl.setAttribute("aria-label", t("rt.spot.mapPickList"));
        this.mapSpotUiEl.append(this.mapSpotHintEl, cardWrap, this.mapSpotPickListEl);
      }
      block.append(this.mapSpotUiEl);
      this.rebuildMapSpotPickList();
      this.syncMapSpotPanel();
    }
    if (this.editable && !readOnlyPreview && !this.isSpotQuest()) {
      this.map?.setToolbarEnd(button(t("rt.page.edit"), "route-map-edit-btn", () => this.openRouteEditor()));
    } else {
      this.map?.setToolbarEnd(null);
    }
    if (this.editable && !readOnlyPreview && this.placing) {
      const pinName = t(this.placing === "start" ? "rt.page.start" : "rt.page.finish");
      block.append(el("span", "qp-map-tip", t("rt.set.tapHint", { pin: pinName })));
    }
    requestAnimationFrame(() => {
      this.map?.resize();
      this.map?.refitForOverlays();
    });
    return block;
  }

  private missionSpotMapPins(): ReturnType<typeof questSpotMapPinForSpotTrace>[] {
    const pins: ReturnType<typeof questSpotMapPinForSpotTrace>[] = [];
    this.q.checkpoints.forEach((spot, index) => {
      if (isRouteEndpointCheckpoint(spot.kind)) return;
      if (!spotTraceHasLocation(spot)) return;
      const thumb = this.spotThumbMarkerUrl(index);
      pins.push(questSpotMapPinForSpotTrace({ ...spot, thumbnail_image: thumb }, index));
    });
    return pins;
  }

  private mountEditorRouteMap(container: HTMLElement): void {
    if (this.editorRouteMapEl?.parentElement !== container) {
      this.dropEditorRouteMap();
      this.editorRouteMapEl = el("div", "qp-map qe-route-map");
      container.append(this.editorRouteMapEl);
      this.editorRouteMap = new RouteMap(this.editorRouteMapEl, [this.q.center_lng, this.q.center_lat], {
        onPlace: (kind, point) => this.placePin(kind, point),
        onMiss: () => this.opts.onToast(t("rt.set.miss")),
        onSpotTap: undefined,
        spotTraceMode: this.isSpotQuest(),
        resetViewLabel: this.isSpotQuest() ? "rt.map.resetSpots" : undefined,
        fitExtraTop: () => {
          const block = this.editorRouteMapEl?.parentElement;
          if (!block) return [];
          return [...block.querySelectorAll<HTMLElement>(".qp-map-tip")];
        },
      });
    }
    this.syncMap();
    requestAnimationFrame(() => {
      this.editorRouteMap?.resize();
      this.editorRouteMap?.refitForOverlays();
    });
  }

  private dropEditorRouteMap(): void {
    this.editorRouteMap?.destroy();
    this.editorRouteMap = null;
    this.editorRouteMapEl = null;
  }

  private syncMapState(map: RouteMap | null): void {
    if (!map) return;
    const line = this.pathLine();
    map.setRoute(
      line,
      line && this.line() ? this.q.route?.canvas ?? null : null,
      line && this.line() ? this.q.route?.paint ?? null : null,
      line && this.line() ? this.q.route?.fill ?? null : null,
    );
    const spotCheckpoints = this.isSpotQuest()
      ? this.pageSpots()
          .map((spot, index) => ({ spot, index }))
          .filter(({ spot }) => spotTraceHasLocation(spot))
          .map(({ spot, index }) => {
            const thumb = this.spotThumbMarkerUrl(index);
            return questSpotMapPinForSpotTrace({ ...spot, thumbnail_image: thumb }, index);
          })
      : this.missionSpotMapPins();
    const s = this.shownPin("start");
    const f = this.shownPin("finish");
    map.setPins({
      start: s ? [s.lng, s.lat] : null,
      finish: f ? [f.lng, f.lat] : null,
      placing: this.placing,
      spotCheckpoints,
      spotPinDisplay: this.isSpotQuest() ? this.spotMapPinDisplay() : "routeOrdinal",
      selectedSpotIndex: this.spotMapInteractive() ? this.mapSelectedSpotIndex : null,
    });
    map.setPlacing(this.editable ? this.placing : null);
  }

  private syncMap(): void {
    this.syncMapState(this.map);
    this.syncMapState(this.editorRouteMap);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.isSpotQuest()) {
          this.map?.invalidateFit();
          this.map?.relayout();
          this.editorRouteMap?.invalidateFit();
          this.editorRouteMap?.relayout();
        } else {
          this.map?.refitForOverlays();
          this.editorRouteMap?.refitForOverlays();
        }
      });
    });
  }

  private spotsEl(line: LngLat[], spots: QuestSpot[]): HTMLElement {
    const section = el("section", "qp-spots");
    section.append(el("h2", "qp-h2", t("rt.page.spots")));
    const startSpot = this.shownPin("start");
    const startAlong = startSpot ? snapToLine([startSpot.lng, startSpot.lat], line)?.alongM ?? 0 : 0;
    const total = this.q.route?.length_m ?? 0;
    for (const spot of spots) {
      const kind = spot.kind === "start" || spot.kind === "finish" ? spot.kind : "spot";
      const row = el("div", `qp-spot${this.placing === kind ? " is-placing" : ""}`);
      const badge = el("span", `qp-spot-pin qp-spot-pin-${kind}`, kind === "start" ? "S" : kind === "finish" ? "F" : "•");
      const text = el("span", "qp-spot-text");
      const name = kind === "start" ? t("rt.page.start") : kind === "finish" ? t("rt.page.finish") : spot.title ?? "";
      let minutes = 0;
      if (kind !== "start") {
        const along = snapToLine([spot.lng, spot.lat], line)?.alongM ?? 0;
        let walked = along - startAlong;
        if (walked <= 0) walked = this.q.route?.closed ? total + walked : Math.abs(walked);
        minutes = walkMinutes(walked);
      }
      text.append(
        el("span", "qp-spot-name", name),
        el("span", "qp-spot-meta", kind === "start" ? t("eta.minutes", { count: 0 }) : `~${duration(minutes)}`),
      );
      row.append(badge, text);
      section.append(row);
    }
    return section;
  }

  private spotTraceSpotsSection(spots: QuestSpot[]): HTMLElement {
    return spotTracePreviewSection(spots, {
      onEmbed: (embed) => this.spotPreviewEmbeds.push(embed),
    });
  }

  private spotMapInteractive(): boolean {
    if (!this.isSpotQuest()) return false;
    const order = this.editable
      ? this.sectionOrder
      : resolveEditorSectionOrder(this.q.page_order ?? null, this.orderContext());
    return order.includes(QUEST_SECTION_MAP);
  }

  private selectMapSpot(index: number | null): void {
    if (!this.spotMapInteractive()) return;
    if (index != null) {
      const spot = this.q.checkpoints[index];
      if (!spot || !spotTraceHasLocation(spot)) return;
      this.highlightSection = spotSectionId(index);
    } else if (parseSpotSectionId(this.highlightSection ?? "") != null) {
      this.highlightSection = null;
    }
    this.mapSelectedSpotIndex = index;
    this.syncMapSpotPanel();
    this.syncMap();
    if (this.editable && this.editorEl) syncQuestEditorListChrome(this.editorColumnsHost(), this.editorEl);
    this.opts.onSpotMapSelectionChange?.(index);
  }

  /** Home background map tapped a spot marker — same as the panel map. */
  selectSpotFromMap(index: number | null): void {
    this.selectMapSpot(index);
  }

  private syncMapSpotPanel(): void {
    if (!this.spotMapInteractive() || !this.mapSpotUiEl) return;
    const hint = this.mapSpotHintEl;
    const wrap = this.mapSpotCardWrapEl;
    const host = this.mapSpotCardHost;
    if (!hint || !wrap || !host) return;
    const idx = this.mapSelectedSpotIndex;
    if (idx == null || !this.q.checkpoints[idx]) {
      hint.hidden = false;
      wrap.classList.add("is-hidden");
      this.mapSpotCardEmbed?.destroy();
      this.mapSpotCardEmbed = null;
      host.replaceChildren();
      this.rebuildMapSpotPickList();
      return;
    }
    hint.hidden = true;
    wrap.classList.remove("is-hidden");
    const previewSpot = this.previewSpotForIndex(idx);
    this.mapSpotCardEmbed?.destroy();
    this.mapSpotCardEmbed = null;
    host.replaceChildren(
      buildSpotTracePreviewCard(previewSpot, {
        onEmbed: (embed) => {
          this.mapSpotCardEmbed = embed;
        },
      }),
    );
    requestAnimationFrame(() => {
      this.mapSpotCardEmbed?.resize();
      this.map?.resize();
      this.editorRouteMap?.resize();
    });
    this.rebuildMapSpotPickList();
  }

  private rebuildMapSpotPickList(): void {
    const list = this.mapSpotPickListEl;
    if (!list) return;
    list.replaceChildren();
    for (let index = 0; index < this.q.checkpoints.length; index++) {
      const spot = this.q.checkpoints[index];
      if (!spot || !spotTraceHasLocation(spot)) continue;
      const li = document.createElement("li");
      const btn = button(spot.title?.trim() || t("quests.untitled"), "qp-spot-map-pick", () => this.selectMapSpot(index));
      btn.type = "button";
      if (this.mapSelectedSpotIndex === index) btn.classList.add("is-active");
      btn.setAttribute("aria-pressed", this.mapSelectedSpotIndex === index ? "true" : "false");
      li.append(btn);
      list.append(li);
    }
  }

  private spotMapPinDisplay(): "spotTitleLabel" | "spotThumbnail" {
    if (this.isSpotQuest()) return "spotThumbnail";
    return "spotTitleLabel";
  }

  private settingsSummary(): string {
    return questSettingsSummary(this.q);
  }

  // ---------- guide ----------

  private currentChunk(): ChunkId | null {
    if (this.modal) {
      if (this.modal.kind === "settings" || this.modal.kind === "title") return this.modal.kind;
      if (this.modal.kind === "spots" && !this.isSpotQuest()) return "spots";
      return null;
    }
    if (this.isSpotQuest()) {
      if (!this.q.title.trim()) return "title";
      return null;
    }
    const p = this.progress();
    if (!p.route) return "route";
    if (!p.settings) return "settings";
    if (!p.title) return "title";
    return null;
  }

  private progress() {
    return routeTraceProgress({
      title: this.q.title,
      has_route: Boolean(this.line()),
      route_settings_done: Boolean(this.q.route_settings_done),
    });
  }

  private renderGuide(): void {
    const guide = this.guideEl;
    guide.replaceChildren();
    if (!this.editable) {
      if (this.opts.mode === "edit" && this.q.status !== "draft") guide.append(this.publishedCard());
      return;
    }
    const card = el("div", `qe-guide${this.modal ? " is-dimmed" : ""}`);
    if (this.adminLiveEdit) {
      const wasCollapsed = this.adminBannerCollapse?.isCollapsed() === true;
      this.adminBannerCollapse?.destroy();
      const liveBanner = createAdminPublishedBanner(this.q.title);
      const roots: (HTMLElement | Document)[] = [this.guideEl, this.pageEl, document];
      if (this.mapEl) roots.push(this.mapEl);
      card.append(liveBanner);
      this.adminBannerCollapse = mountCollapseBanner(liveBanner, roots, {
        initiallyCollapsed: wasCollapsed,
      });
    }
    const ready = this.isSpotQuest()
      ? spotTraceReady({ title: this.q.title, checkpoints: this.q.checkpoints })
      : routeTraceReady({
          title: this.q.title,
          has_route: Boolean(this.line()),
          route_settings_done: Boolean(this.q.route_settings_done),
        });
    card.append(
      el("p", "qe-kicker", t(this.isSpotQuest() ? "rt.spot.kicker" : "rt.kicker")),
      el(
        "h1",
        "qe-heading",
        ready ? t("rt.headingReady") : t(this.isSpotQuest() ? "rt.spot.heading" : "rt.heading"),
      ),
      el("p", "qe-sub", ready ? t("rt.subReady") : t(this.isSpotQuest() ? "rt.spot.sub" : "rt.sub")),
    );
    if (this.isSpotQuest()) {
      const addSpot = button(t("rt.spots.add"), "rt-spot-add-btn", () => this.appendSpotDraft());
      card.append(addSpot);
    }
    const list = el("ol", "rt-chunks");
    const current = this.currentChunk();
    let n = 0;
    const add = (id: ChunkId, opts: { required?: boolean; soon?: boolean }) => {
      n += 1;
      list.append(this.chunkEl(id, n, current === id, opts));
    };
    if (this.isSpotQuest()) {
      add("title", { required: true });
    } else {
      add("route", { required: true });
      add("settings", { required: true });
      add("title", { required: true });
    }
    const divider = el("li", "rt-divider");
    divider.append(el("span", "", t("rt.optionalDivider")));
    list.append(divider);
    add("description", {});
    if (!this.isSpotQuest()) {
      add("mission", { soon: true });
      add("secrets", { soon: true });
    }
    add("story", {});
    add("manners", {});
    if (!this.isSpotQuest()) add("decorate", { soon: true });
    card.append(list);

    guide.append(card);
    if (this.modal) {
      this.dropSpotLocPicker();
      guide.append(this.modalEl());
    }
  }

  private chunkEl(id: ChunkId, n: number, current: boolean, opts: { required?: boolean; soon?: boolean }): HTMLElement {
    const li = el("li", "rt-chunk");
    const row = el("div", `rt-chunk-row${current ? " is-current" : ""}${opts.soon ? " is-soon" : ""}${opts.required ? "" : " is-optional"}`);
    row.append(el("span", "rt-chunk-num", String(n)));
    const body = el("div", "rt-chunk-body");
    body.append(el("span", "rt-chunk-title", t(`rt.${id}` as MessageKey)));
    const summary = opts.soon ? null : this.chunkSummary(id);
    body.append(el("span", summary ? "rt-chunk-summary" : "rt-chunk-desc", summary ?? this.chunkDesc(id)));
    if (id === "route" && !this.mapBlock && !this.line()) {
      body.append(button(t("rt.route.add"), "rt-add-route", () => this.addMapBlock()));
    }
    row.append(body);
    if (opts.required) row.append(el("span", "rt-tag", t("rt.required")));
    if (opts.soon) row.append(el("span", "rt-tag rt-tag-soon", t("rt.soon")));
    const enabled =
      !opts.soon &&
      !(id === "settings" && !this.line()) &&
      !(id === "route" && this.isSpotQuest());
    if (enabled) {
      row.append(el("span", "rt-chunk-chev", "›"));
      row.tabIndex = 0;
      row.setAttribute("role", "button");
      row.dataset.chunk = id;
      const open = (e: Event) => {
        if ((e.target as HTMLElement).closest(".rt-add-route")) return;
        this.openChunk(id);
      };
      row.addEventListener("click", open);
      row.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open(e);
        }
      });
    } else {
      row.setAttribute("aria-disabled", "true");
    }
    li.append(row);
    return li;
  }

  private chunkDesc(id: ChunkId): string {
    if (id === "spots") return t("rt.spots.desc");
    if (id === "route" && this.mapBlock && !this.line()) return t("rt.route.mapAdded");
    if (id === "settings" && !this.line()) return t("rt.settings.needRoute");
    return t(`rt.${id}.desc` as MessageKey);
  }

  private chunkSummary(id: ChunkId): string | null {
    switch (id) {
      case "spots":
        if (!this.q.checkpoints.length) return null;
        return t("rt.page.spotCount", { count: this.q.checkpoints.length });
      case "route":
        return this.line() ? t("rt.route.summary", { distance: formatDistance(this.q.route?.length_m ?? 0) }) : null;
      case "settings":
        return this.line() && this.q.route_settings_done ? this.settingsSummary() : null;
      case "title":
        return this.q.title.trim() ? `“${this.q.title.trim()}”` : null;
      case "description":
        return this.q.description?.trim() ? clip(this.q.description, 46) : null;
      case "story":
        return this.q.story?.trim() ? `“${clip(this.q.story, 42)}”` : null;
      case "manners":
        return this.q.manners?.trim() ? clip(this.q.manners, 46) : null;
      default:
        return null;
    }
  }

  private publishedCard(): HTMLElement {
    const card = el("div", "qe-guide");
    card.append(
      el("p", "qe-kicker", t(this.isSpotQuest() ? "rt.spot.kicker" : "rt.kicker")),
      el("h1", "qe-heading", t("rt.published.heading")),
      el("p", "qe-sub", t("rt.published.sub")),
    );
    return card;
  }

  // ---------- step windows ----------

  private openChunk(id: ChunkId): void {
    if (id === "spots") {
      if (this.isSpotQuest()) {
        this.appendSpotDraft();
        return;
      }
      this.modal = { kind: "spots", index: this.q.checkpoints.length ? 0 : null };
      this.enterStep();
      this.render();
      this.focusModal();
      return;
    }
    if (id === "route") {
      if (this.mapBlock || this.line()) this.openRouteEditor();
      else this.addMapBlock();
      return;
    }
    if (id === "settings") this.modal = { kind: "settings", step: 0 };
    else if (id === "title") this.modal = { kind: "title", step: 0 };
    else if (id === "description") this.modal = { kind: "description" };
    else if (id === "story") this.modal = { kind: "story" };
    else if (id === "manners") this.modal = { kind: "manners" };
    else return;
    this.enterStep();
    this.render();
    this.focusModal();
  }

  private closeModal(): void {
    if (this.modal?.kind === "title") this.titleSave.commit();
    this.modal = null;
    this.modalTitleInput = null;
    this.placing = null;
    this.spotLocOpen = false;
    this.dropSpotLocPicker();
    this.render();
  }

  private dropSpotLocPicker(): void {
    this.spotLocPicker?.destroy();
    this.spotLocPicker = null;
  }

  private render(): void {
    this.renderPage();
    if (this.editable) this.renderEditorColumns();
    else this.renderGuide();
    this.paintQuestBar();
    this.notifyEditorStateChanged();
  }

  private focusModal(): void {
    requestAnimationFrame(() => {
      const target = this.guideEl.querySelector<HTMLElement>(".rt-modal input, .rt-modal textarea, .rt-modal .rt-choice.is-on, .rt-modal .rt-next");
      target?.focus();
    });
  }

  private settingsStep(): SettingsStep | null {
    if (this.modal?.kind !== "settings") return null;
    return settingsSteps(Boolean(this.q.start_fixed))[this.modal.step] ?? null;
  }

  private enterStep(): void {
    this.placing = null;
    if (this.settingsStep() === "place") {
      this.placing = this.placed.has("start") ? (this.placed.has("finish") ? null : "finish") : "start";
    }
  }

  private go(delta: number): void {
    const m = this.modal;
    if (!m) return;
    if (m.kind === "settings") {
      const steps = settingsSteps(Boolean(this.q.start_fixed));
      const next = m.step + delta;
      if (next < 0) return this.closeModal();
      if (next >= steps.length) {
        this.q.route_settings_done = true;
        this.scheduleSave(0);
        return this.closeModal();
      }
      m.step = next;
    } else if (m.kind === "title") {
      this.titleSave.commit();
      const next = m.step + delta;
      if (next < 0 || next > 1) return this.closeModal();
      if (delta > 0 && !this.q.title.trim()) return;
      m.step = next;
    } else {
      return this.closeModal();
    }
    this.enterStep();
    this.render();
    this.focusModal();
  }

  private modalEl(): HTMLElement {
    const m = this.modal!;
    const box = el("div", "rt-modal");
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");
    let total = 1;
    let index = 0;
    let kicker = "";
    let last = true;
    let nextDisabled = false;
    const body = el("div", "rt-modal-body");
    if (m.kind === "settings") {
      const steps = settingsSteps(Boolean(this.q.start_fixed));
      total = steps.length;
      index = m.step;
      last = m.step === steps.length - 1;
      const step = steps[m.step]!;
      if (step === "startFinish") {
        kicker = t("rt.set.kicker1");
        body.append(el("h2", "rt-q", t("rt.set.q1")));
        body.append(
          choiceGroup([
            [t("rt.set.anywhere"), !this.q.start_fixed, () => this.setStartFixed(false)],
            [t("rt.set.fixed"), Boolean(this.q.start_fixed), () => this.setStartFixed(true)],
          ]),
        );
        body.append(el("p", "rt-note", t(this.q.start_fixed ? "rt.set.fixedNote" : "rt.set.anywhereNote")));
      } else if (step === "place") {
        kicker = t("rt.set.kicker2");
        body.append(el("h2", "rt-q", t("rt.set.q2")), el("p", "rt-sub", t("rt.set.sub2")));
        body.append(this.placeRow("start"), this.placeRow("finish"));
      } else {
        kicker = t("rt.set.kicker3");
        body.append(el("h2", "rt-q", t("rt.set.q3")));
        const on = this.q.time_limit_s != null;
        body.append(
          choiceGroup([
            [t("rt.set.noLimit"), !on, () => this.setTimeLimit(null)],
            [t("rt.set.limit"), on, () => this.setTimeLimit(this.suggestedLimit())],
          ]),
        );
        if (on) {
          const row = el("label", "rt-limit");
          const input = el("input", "rt-limit-input");
          input.type = "number";
          input.min = String(MIN_TIME_LIMIT_MIN);
          input.max = String(MAX_TIME_LIMIT_MIN);
          input.inputMode = "numeric";
          input.value = String(Math.round((this.q.time_limit_s ?? 0) / 60));
          input.addEventListener("input", () => {
            const v = Math.round(Number(input.value));
            if (!Number.isFinite(v) || v < MIN_TIME_LIMIT_MIN || v > MAX_TIME_LIMIT_MIN) return;
            this.q.time_limit_s = v * 60;
            this.renderPage();
            this.scheduleSave();
          });
          row.append(el("span", "", t("rt.set.within")), input, el("span", "", t("rt.set.min")));
          body.append(row);
        }
      }
    } else if (m.kind === "spots") {
      kicker = t("rt.spots.kicker");
      body.append(el("h2", "rt-q", t("rt.spots.q")));
      body.append(el("p", "rt-sub", t("rt.spots.tapHint")));
      const index = m.index ?? (this.q.checkpoints.length ? 0 : null);
      if (index == null) {
        body.append(
          button(t("rt.spots.add"), "rt-choice", () => this.appendSpotDraft()),
          button(t("rt.spots.addMap"), "rt-choice", () => {
            this.closeModal();
            this.opts.onToast(t("rt.spots.tapToast"));
          }),
        );
      } else if (this.spotLocOpen) {
        const spot = this.q.checkpoints[index]!;
        const host = el("div", "rt-spot-loc-host");
        body.append(
          button(t("rt.back"), "rt-back", () => {
            this.spotLocOpen = false;
            this.render();
            this.focusModal();
          }),
          host,
        );
        this.spotLocPicker = new SpotLocationPicker({
          container: host,
          initial: spotTraceHasLocation(spot) ? [spot.lng, spot.lat] : [this.q.center_lng, this.q.center_lat],
          onConfirm: (point) => {
            spot.lng = point[0];
            spot.lat = point[1];
            this.spotLocOpen = false;
            this.syncMap();
            this.scheduleSave();
            this.render();
            this.focusModal();
          },
          onToast: (message) => this.opts.onToast(message),
        });
      } else {
        const tabs = el("div", "rt-spot-tabs");
        this.q.checkpoints.forEach((spot, i) => {
          const label = spot.title?.trim() || t("rt.spots.titlePh");
          const tab = button(label, `rt-spot-tab${i === index ? " is-on" : ""}`, () => {
            m.index = i;
            this.render();
            this.focusModal();
          });
          tabs.append(tab);
        });
        body.append(tabs);
        const spot = this.q.checkpoints[index]!;
        const form = el("div", "rt-spot-form");
        const title = el("input", "rt-text");
        title.maxLength = 80;
        title.placeholder = t("rt.spots.titlePh");
        title.value = spot.title ?? "";
        title.addEventListener("input", () => {
          spot.title = title.value;
          this.syncMap();
          this.scheduleSave();
        });
        const desc = el("textarea", "rt-textarea");
        desc.maxLength = MAX_SPOT_LABEL;
        desc.placeholder = t("rt.spots.descPh");
        desc.value = spot.label ?? "";
        desc.addEventListener("input", () => {
          spot.label = desc.value.trim() || null;
          this.scheduleSave();
        });
        const thumbRow = el("div", "rt-spot-thumb-row");
        const thumbBtn = button(
          spot.thumbnail_image ? t("rt.spots.thumbReplace") : t("rt.spots.thumbUpload"),
          "rt-choice rt-upload",
          () => fileInput.click(),
        );
        const fileInput = el("input", "") as HTMLInputElement;
        fileInput.type = "file";
        fileInput.accept = "image/jpeg,image/webp,image/png";
        fileInput.hidden = true;
        fileInput.addEventListener("change", () => {
          void this.uploadSpotThumb(index, spot, fileInput);
        });
        if (spot.thumbnail_image) {
          const img = el("img", "rt-spot-thumb-preview") as HTMLImageElement;
          img.src = spot.thumbnail_image;
          img.alt = "";
          thumbRow.append(img);
        }
        thumbRow.append(thumbBtn, fileInput);
        const locRow = el("div", "rt-spot-loc-row");
        locRow.append(
          el("span", "rt-spot-loc-label", t("rt.spots.location")),
          button(t("rt.spots.locationEdit"), "rt-choice", () => {
            this.spotLocOpen = true;
            m.index = index;
            this.render();
            this.focusModal();
          }),
        );
        const del = button(t("rt.spots.delete"), "rt-spot-del", () => {
          this.q.checkpoints = this.q.checkpoints.filter((_, i) => i !== index);
          m.index = this.q.checkpoints.length ? Math.min(index, this.q.checkpoints.length - 1) : null;
          this.render();
          this.scheduleSave();
        });
        form.append(
          labeledField(t("quest.spot.title"), title),
          labeledField(t("rt.description"), desc),
          labeledField(t("rt.spots.thumb"), thumbRow),
          locRow,
          del,
        );
        body.append(form);
        body.append(
          button(t("rt.spots.add"), "rt-choice", () => {
            this.appendSpotDraft();
            m.index = this.q.checkpoints.length - 1;
          }),
        );
      }
    } else if (m.kind === "title") {
      total = 2;
      index = m.step;
      last = m.step === 1;
      if (m.step === 0) {
        kicker = t("rt.ttl.kicker1");
        body.append(el("h2", "rt-q", t("rt.ttl.q1")));
        let input = this.modalTitleInput;
        if (!input) {
          input = el("input", "rt-text");
          input.maxLength = 80;
          this.titleSave.bindComposition(input);
          input.addEventListener("input", () => {
            this.q.title = input!.value;
            this.titleSave.noteEdit();
            this.syncTitleDraftUi();
          });
          input.addEventListener("blur", () => {
            this.titleSave.commit();
            this.renderPage();
          });
          input.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              this.titleSave.commit();
              this.go(1);
            }
          });
          this.modalTitleInput = input;
        }
        if (document.activeElement !== input) input.value = this.q.title;
        body.append(input);
        nextDisabled = !this.q.title.trim();
      } else {
        kicker = t("rt.ttl.kicker2");
        body.append(el("h2", "rt-q", t("rt.ttl.q2")), el("p", "rt-sub", t("rt.ttl.coverSub")));
        const upload = button(this.q.cover_image ? t("cover.replace") : t("rt.ttl.upload"), "rt-choice rt-upload", () => this.openCoverPicker());
        body.append(upload, el("p", "rt-note", t("cover.notice")));
      }
    } else {
      const kind: TextKind = m.kind;
      kicker = t(`rt.${kind}.kicker`);
      body.append(el("h2", "rt-q", t(`rt.${kind}.q`)));
      const ta = el("textarea", "rt-textarea");
      ta.maxLength = TEXT_MAX[kind];
      if (kind === "description") ta.placeholder = t("rt.description.placeholder");
      ta.value = this.q[kind] ?? "";
      ta.addEventListener("input", () => {
        this.q[kind] = ta.value;
        this.renderPage();
        this.scheduleSave();
      });
      body.append(ta);
    }

    const head = el("div", "rt-modal-head");
    head.append(el("span", "rt-modal-kicker", kicker));
    if (total > 1) {
      const counter = el("span", "rt-counter", t("rt.step", { n: index + 1, total }));
      const dots = el("span", "rt-dots");
      for (let i = 0; i < total; i++) dots.append(el("span", i <= index ? "rt-dot is-on" : "rt-dot"));
      counter.append(dots);
      head.append(counter);
    }
    const foot = el("div", "rt-modal-foot");
    const skipLabel = m.kind === "title" && m.step === 1 ? t("rt.ttl.skip") : null;
    foot.append(button(skipLabel ?? t("rt.back"), "rt-back", () => (skipLabel ? this.go(1) : this.go(-1))));
    const next = button(last ? t("rt.done") : t("rt.next"), "rt-next", () => this.go(1));
    next.disabled = nextDisabled;
    foot.append(next);
    box.append(head, body, foot);
    return box;
  }

  private placeRow(kind: PinKind): HTMLElement {
    const spot = this.pin(kind);
    const active = this.placing === kind;
    const row = button("", `rt-place${active ? " is-active" : ""}`, () => {
      this.placing = kind;
      this.render();
    });
    const badge = el("span", `qp-spot-pin qp-spot-pin-${kind}${active ? " is-placing" : ""}`, kind === "start" ? "S" : "F");
    const text = el("span", "rt-place-text");
    text.append(el("span", "rt-place-name", t(kind === "start" ? "rt.page.start" : "rt.page.finish")));
    let state = "";
    if (active) state = t(kind === "start" ? "rt.set.placingStart" : "rt.set.placingFinish");
    else if (this.placed.has(kind) && spot) state = t("rt.set.placed");
    if (state) text.append(el("span", active ? "rt-place-state is-active" : "rt-place-state", state));
    row.append(badge, text);
    if (active) row.append(el("span", "rt-place-flag", t("rt.set.placing")));
    else if (this.placed.has(kind)) row.append(el("span", "rt-place-check", "✓"));
    return row;
  }

  private handleKey(e: KeyboardEvent): void {
    if (this.editable && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      void this.saveFromShortcut();
      return;
    }
    if (!this.modal) return;
    if (e.key === "Escape") {
      e.preventDefault();
      if (this.modal.kind === "settings" && this.placing) {
        this.placing = null;
        this.render();
        return;
      }
      this.closeModal();
    }
  }

  // ---------- edits ----------

  private setStartFixed(fixed: boolean): void {
    this.q.start_fixed = fixed;
    if (!fixed) {
      const line = this.line();
      if (line) this.setPinsAt(line[0]!, line[line.length - 1]!);
      this.placed.clear();
    }
    this.q.route_settings_done = true;
    this.render();
    this.scheduleSave();
  }

  private setTimeLimit(seconds: number | null): void {
    this.q.time_limit_s = seconds;
    this.q.route_settings_done = true;
    this.render();
    this.scheduleSave();
    if (this.modal?.kind === "settings") this.focusModal();
  }

  private suggestedLimit(): number {
    const walk = walkMinutes(this.q.route?.length_m ?? 0);
    const minutes = Math.min(MAX_TIME_LIMIT_MIN, Math.max(15, Math.ceil((walk * 1.2) / 15) * 15));
    return minutes * 60;
  }

  private canPlaceRoutePins(): boolean {
    if (!this.editable || !this.q.start_fixed || !this.line()) return false;
    if (this.modal?.kind === "settings" && this.settingsStep() === "place") return true;
    if (!this.modal && this.selectedSection === QUEST_SECTION_ROUTE) return true;
    return false;
  }

  private placePin(kind: PinKind, point: LngLat): void {
    if (!this.canPlaceRoutePins()) return;
    const s = this.pin("start");
    const f = this.pin("finish");
    const line = this.line()!;
    this.setPinsAt(
      kind === "start" ? point : s ? [s.lng, s.lat] : line[0]!,
      kind === "finish" ? point : f ? [f.lng, f.lat] : line[line.length - 1]!,
    );
    this.placed.add(kind);
    this.placing = kind === "start" && !this.placed.has("finish") ? "finish" : null;
    this.render();
    this.scheduleSave();
  }

  private setPinsAt(start: LngLat, finish: LngLat): void {
    const spots = this.q.checkpoints.filter((s) => s.kind !== "start" && s.kind !== "finish");
    this.q.checkpoints = [
      { ...(this.pin("start") ?? blankSpot("start")), lng: start[0], lat: start[1] },
      ...spots,
      { ...(this.pin("finish") ?? blankSpot("finish")), lng: finish[0], lat: finish[1] },
    ];
  }

  private removeSpotAt(index: number): void {
    this.spotThumbPending = this.remapPendingIndexMap(this.spotThumbPending, index);
    this.spotImagesPending = this.remapPendingIndexMap(this.spotImagesPending, index);
    this.spotThumbBust = this.remapPendingIndexMap(this.spotThumbBust, index);
    this.q.checkpoints = this.q.checkpoints.filter((_, i) => i !== index);
    this.q.checkpoints.forEach((cp, i) => {
      cp.seq = i;
    });
    this.syncMap();
    this.map?.invalidateFit();
  }

  private remapPendingIndexMap<T>(map: Map<number, T>, removed: number): Map<number, T> {
    const next = new Map<number, T>();
    for (const [i, value] of map) {
      if (i === removed) continue;
      next.set(i > removed ? i - 1 : i, value);
    }
    return next;
  }

  private appendSpotDraft(at?: LngLat): void {
    if (!this.isSpotQuest()) return;
    const lng = at?.[0] ?? 0;
    const lat = at?.[1] ?? 0;
    this.q.checkpoints = [
      ...this.q.checkpoints,
      {
        lat,
        lng,
        radius_m: DEFAULT_SPOT_RADIUS_M,
        label: null,
        needs_media: false,
        title: "",
        est_minutes: null,
        kind: null,
        images: [],
      },
    ];
    this.q.route_settings_done = true;
    const index = this.q.checkpoints.length - 1;
    const spotId = spotSectionId(index);
    if (!this.sectionOrder.includes(spotId)) {
      const titleIdx = this.sectionOrder.indexOf(QUEST_SECTION_TITLE);
      if (titleIdx >= 0) this.sectionOrder.splice(titleIdx + 1, 0, spotId);
      else this.sectionOrder.push(spotId);
    }
    this.syncPageOrderField();
    this.selectedSection = spotId;
    this.highlightSection = spotId;
    this.render();
    this.scheduleSave();
  }

  private appendPhotoDraft(): void {
    if (!this.q.page_photos) this.q.page_photos = [];
    this.q.page_photos.push({ image: null, caption: null });
    const photoId = photoSectionId(this.q.page_photos.length - 1);
    if (!this.sectionOrder.includes(photoId)) this.sectionOrder.push(photoId);
    this.syncPageOrderField();
    this.selectedSection = photoId;
    this.highlightSection = photoId;
    this.render();
    this.scheduleSave();
  }

  private appendPageBlockDraft(kind: PageBlockKind): void {
    if (!this.q.page_blocks) this.q.page_blocks = [];
    this.q.page_blocks.push(createDefaultPageBlock(kind));
    const blockId = blockSectionId(this.q.page_blocks.length - 1);
    if (!this.sectionOrder.includes(blockId)) this.sectionOrder.push(blockId);
    this.syncPageOrderField();
    this.selectedSection = blockId;
    this.highlightSection = blockId;
    this.render();
    this.scheduleSave();
  }

  private async uploadGalleryItem(blockIndex: number, itemIndex: number, input: HTMLInputElement): Promise<void> {
    const block = this.q.page_blocks?.[blockIndex];
    if (!block || block.kind !== "gallery") return;
    const item = block.items[itemIndex];
    if (!item) return;
    await uploadGalleryItemFile(this.q.id, blockIndex, itemIndex, input, item, {
      onToast: (m) => this.opts.onToast(m),
      onSyncPreview: () => this.syncEditableUi(),
      onRenderAll: () => this.render(),
      onScheduleSave: () => this.scheduleSave(),
      onEditorStateChanged: () => this.notifyEditorStateChanged(),
    });
  }

  private async uploadPagePhoto(index: number, photo: QuestPagePhoto, input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    try {
      await this.flushPending();
      photo.image = await uploadQuestPagePhoto(this.q.id, index, file);
      this.render();
      this.notifyEditorStateChanged();
      this.scheduleSave();
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("quest.saveFailed"));
    }
  }

  private async uploadSpotThumb(index: number, _spot: QuestSpot, input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    try {
      const cropped = await cropSpotThumbnail(file);
      if (!cropped) return;
      this.spotThumbUploadInFlight.add(index);
      this.notifyEditorStateChanged();
      const url = await uploadQuestSpotThumbnailPending(this.q.id, index, cropped);
      const bust = (this.spotThumbBust.get(index) ?? 0) + 1;
      this.spotThumbBust.set(index, bust);
      const displayUrl = withSpotThumbnailCacheBust(url, bust);
      this.spotThumbPending.set(index, displayUrl);
      const spot = this.q.checkpoints[index];
      if (!spot) return;
      spot.thumbnail_image = url.split("?")[0] ?? url;
      spot.seq = index;
      this.map?.invalidateFit();
      this.syncMap();
      if (this.editorEl) refreshSpotThumbPreview(this.editorColumnsHost(), this.editorEl, index);
      this.syncEditableUi();
    } catch (err) {
      const msg = err instanceof SpotThumbPickError ? err.message : err instanceof Error ? err.message : t("quest.saveFailed");
      this.opts.onToast(msg);
    } finally {
      this.spotThumbUploadInFlight.delete(index);
      this.notifyEditorStateChanged();
      this.scheduleSave();
    }
  }

  private spotImagesForIndex(index: number): QuestSpotImage[] {
    const spot = this.q.checkpoints[index];
    if (!spot) return [];
    const base = normalizeQuestSpotImages(spot.images);
    const pending = this.spotImagesPending.get(index);
    if (pending?.length) {
      return pending.map((p, i) => ({
        image: p.image,
        caption: p.caption ?? base[i]?.caption ?? null,
      }));
    }
    return base;
  }

  private async uploadSpotImage(index: number, input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    const spot = this.q.checkpoints[index];
    if (!spot) return;
    try {
      const slot = normalizeQuestSpotImages(spot.images).length;
      const url = await uploadQuestSpotPhoto(this.q.id, index, slot, file);
      if (!spot.images) spot.images = [];
      spot.images.push({ image: url, caption: null });
      this.spotImagesPending.set(index, [...normalizeQuestSpotImages(spot.images)]);
      this.syncEditableUi();
      this.notifyEditorStateChanged();
      this.scheduleSave();
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("quest.saveFailed"));
    }
  }

  private async removeSpotImage(index: number, slot: number): Promise<void> {
    const spot = this.q.checkpoints[index];
    if (!normalizeQuestSpotImages(spot?.images)[slot]) return;
    try {
      await deleteQuestSpotPhoto(this.q.id, index, slot);
      spot.images = normalizeQuestSpotImages(spot.images).filter((_, i) => i !== slot);
      this.spotImagesPending.set(index, [...spot.images]);
      this.syncEditableUi();
      this.notifyEditorStateChanged();
      this.scheduleSave();
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("quest.saveFailed"));
    }
  }

  private moveSpotImage(index: number, from: number, dir: -1 | 1): void {
    void this.reorderSpotImages(index, from, dir);
  }

  private async reorderSpotImages(index: number, from: number, dir: -1 | 1): Promise<void> {
    const spot = this.q.checkpoints[index];
    if (!normalizeQuestSpotImages(spot?.images).length) return;
    const to = from + dir;
    const order = normalizeQuestSpotImages(spot.images);
    if (to < 0 || to >= order.length) return;
    const tmp = order[from]!;
    order[from] = order[to]!;
    order[to] = tmp;
    try {
      if (!(await this.flushPending())) return;
      const blobs = await Promise.all(order.map((item) => fetch(item.image).then((r) => r.blob())));
      const urls: string[] = [];
      for (let slot = 0; slot < blobs.length; slot++) {
        urls.push(await uploadQuestSpotPhoto(this.q.id, index, slot, blobs[slot]!));
      }
      for (let slot = blobs.length; slot < blobs.length + 4; slot++) {
        try {
          await deleteQuestSpotPhoto(this.q.id, index, slot);
        } catch {
          break;
        }
      }
      spot.images = order.map((item, slot) => ({ ...item, image: urls[slot]! }));
      this.spotImagesPending.set(index, [...spot.images]);
      this.syncEditableUi();
      this.notifyEditorStateChanged();
      this.scheduleSave();
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("quest.saveFailed"));
    }
  }

  private addMapBlock(): void {
    this.mapBlock = true;
    writeMapBlock(this.q.id, true);
    this.render();
  }

  private openRouteEditor(): void {
    this.opts.onBeforeOpenRouteEditor?.();
    window.open(questRoutePath(this.q.id), questRouteWindowName(this.q.id));
  }

  /** Wire delegated input/change/compositionend on editor + page columns. */
  private bindEditorDirtyDomListeners(): void {
    this.editorDirtyAbort?.abort();
    const ac = new AbortController();
    this.editorDirtyAbort = ac;
    const roots: HTMLElement[] = [];
    if (this.editorEl) roots.push(this.editorEl);
    if (this.pageEl) roots.push(this.pageEl);
    bindQuestEditorDirtyDomListeners(roots, () => this.notifyEditorStateChanged(), ac.signal);
  }

  /** Recompute dirty from full editor snapshot and repaint Save UI. */
  private notifyEditorStateChanged(): void {
    this.paintSaved();
  }

  // ---------- server ----------

  private dirtyOpts(): QuestEditorDirtyOpts {
    return { thumbBust: this.spotThumbBust, thumbUploadInFlight: this.spotThumbUploadInFlight };
  }

  private syncDirtyFromSnapshot(): void {
    this.dirty = questEditorIsDirty(this.persistedSnapshot, this.q, this.dirtyOpts());
  }

  private commitPersistedSnapshot(): void {
    this.persistedSnapshot = questEditorDirtySnapshot(this.q, this.dirtyOpts());
    this.syncDirtyFromSnapshot();
  }

  private spotThumbMarkerUrl(index: number): string | null {
    const raw = this.q.checkpoints[index]?.thumbnail_image ?? null;
    if (raw) return raw.split("?")[0] ?? raw;
    const pending = this.spotThumbPending.get(index);
    return pending ? (pending.split("?")[0] ?? pending) : null;
  }

  private spotThumbDisplayUrl(index: number): string | null {
    const base = this.spotThumbMarkerUrl(index);
    if (!base) return null;
    const bust = this.spotThumbBust.get(index) ?? 0;
    return bust > 0 ? withSpotThumbnailCacheBust(base, bust) : base;
  }

  private scheduleSave(delay = SAVE_DELAY_MS): void {
    this.notifyEditorStateChanged();
    if (!this.dirty) {
      window.clearTimeout(this.saveTimer);
      return;
    }
    if (this.adminLiveEdit) return;
    if (!readQuestEditorAutosavePref()) {
      window.clearTimeout(this.saveTimer);
      return;
    }
    window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => void this.flush(), delay);
  }

  /** Save now (before a reload into another language). False when the save didn't land. */
  async flushPending(): Promise<boolean> {
    if (!this.editable) return true;
    this.titleSave.commit();
    window.clearTimeout(this.saveTimer);
    this.syncDirtyFromSnapshot();
    for (let i = 0; i < 50 && (this.dirty || this.saving); i++) {
      if (this.saving) {
        await new Promise((resolve) => window.setTimeout(resolve, 100));
        continue;
      }
      await this.flush();
      window.clearTimeout(this.saveTimer);
    }
    return !this.dirty && !this.saving && !this.saveFailed;
  }

  private async saveFromShortcut(): Promise<void> {
    this.titleSave.commit();
    const ok = await this.flushPending();
    if (!ok) {
      this.opts.onToast(t("qbar.saveFailed"));
      return;
    }
    this.opts.onToast(this.adminLiveEdit ? t("qbar.liveSavedJustNow") : t("qbar.savedJustNow"));
  }

  private async flush(): Promise<void> {
    if (this.saving) {
      this.scheduleSave();
      return;
    }
    if (this.adminLiveEdit && !confirmAdminPublishedSave(t("rt.adminPublished.banner"))) return;
    this.syncPageOrderField();
    this.saving = true;
    this.dirty = false;
    this.paintSaved();
    try {
      const saved = await updateMission(this.q.id, toWriteBody(this.q));
      this.saveFailed = false;
      this.savedAt = Date.now();
      this.adoptServer(saved);
      this.sectionOrder = resolveEditorSectionOrder(saved.page_order ?? null, this.orderContext());
      this.syncPageOrderField();
      this.commitPersistedSnapshot();
    } catch (err) {
      this.saveFailed = true;
      this.syncDirtyFromSnapshot();
      this.savedAt = null;
      this.opts.onToast(err instanceof Error ? err.message : t("qbar.saveFailed"));
    } finally {
      this.saving = false;
      this.paintSaved();
    }
  }

  /** Server-owned fields (route, snapped pins) come back; typed text stays as the creator left it. */
  private adoptServer(saved: QuestDetail): void {
    const routeChanged = JSON.stringify(saved.route ?? null) !== JSON.stringify(this.q.route ?? null);
    this.q.route = saved.route ?? null;
    this.q.has_route = saved.has_route;
    this.q.route_place = saved.route_place ?? null;
    this.q.route_piece_id = saved.route_piece_id;
    if (this.isSpotQuest()) {
      this.q.checkpoints = saved.checkpoints.map((s, i) => {
        const prev = this.q.checkpoints[i];
        const pendingThumb = this.spotThumbPending.get(i);
        const pendingImages = this.spotImagesPending.get(i);
        if (s.thumbnail_image) this.spotThumbPending.delete(i);
        if (s.images?.length) this.spotImagesPending.delete(i);
        if (prev && spotTraceHasLocation(prev) && !spotTraceHasLocation(s)) {
          return {
            ...s,
            lat: prev.lat,
            lng: prev.lng,
            thumbnail_image: s.thumbnail_image ?? prev.thumbnail_image ?? pendingThumb ?? null,
            images: s.images?.length ? s.images : pendingImages ?? prev.images ?? [],
          };
        }
        if (prev?.thumbnail_image && !s.thumbnail_image) {
          return {
            ...s,
            thumbnail_image: prev.thumbnail_image,
            images: s.images?.length ? s.images : pendingImages ?? prev.images ?? s.images ?? [],
          };
        }
        if (pendingThumb && !s.thumbnail_image) {
          return {
            ...s,
            thumbnail_image: pendingThumb,
            images: s.images?.length ? s.images : pendingImages ?? prev?.images ?? [],
          };
        }
        if (pendingImages?.length && !s.images?.length) {
          return { ...s, images: pendingImages };
        }
        return s;
      });
    } else {
      this.q.checkpoints = saved.checkpoints;
    }
    this.q.status = saved.status;
    this.q.source_lang = saved.source_lang;
    this.q.source_lang_auto = saved.source_lang_auto;
    if (saved.page_photos) this.q.page_photos = saved.page_photos;
    if (saved.page_blocks) this.q.page_blocks = saved.page_blocks;
    if (saved.page_order !== undefined) this.q.page_order = saved.page_order ?? null;
    if (routeChanged) this.mapBlock = this.mapBlock || Boolean(saved.has_route);
    // Editable: rebuild middle-column inputs so they bind current checkpoints (avoid stale refs after save).
    if (this.editable || !this.modal || routeChanged) this.render();
    else this.renderPage();
  }

  private async refetch(announce = false): Promise<void> {
    if (!this.editable) return;
    try {
      const fresh = await getMission(this.q.id);
      const placeChanged = JSON.stringify(fresh.route_place ?? null) !== JSON.stringify(this.q.route_place ?? null);
      const changed = JSON.stringify(fresh.route ?? null) !== JSON.stringify(this.q.route ?? null);
      if (!changed) {
        if (placeChanged) {
          this.q.route_place = fresh.route_place ?? null;
          if (this.editable) this.render();
          else this.renderPage();
        }
        return;
      }
      if (this.dirty || this.saving) {
        this.q.route = fresh.route ?? null;
        this.q.has_route = fresh.has_route;
        this.q.route_place = fresh.route_place ?? null;
        this.scheduleSave(0);
      } else {
        this.adoptServer(fresh);
        this.commitPersistedSnapshot();
        this.notifyEditorStateChanged();
      }
      if (announce) this.opts.onToast(t("rt.routeSaved"));
    } catch {
      /* stay on the current copy */
    }
  }

  private async publish(): Promise<void> {
    if (!window.confirm(t("quest.publishConfirm"))) return;
    window.clearTimeout(this.saveTimer);
    try {
      await updateMission(this.q.id, toWriteBody(this.q));
      this.q = await publishMission(this.q.id);
      this.commitPersistedSnapshot();
      writeMapBlock(this.q.id, false);
      this.opts.onToast(t("quest.published"));
      this.destroyListeners();
      this.renderShell();
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("quest.publishFailed"));
    }
  }

  private destroyListeners(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    window.removeEventListener("focus", this.onFocus);
    window.removeEventListener("keydown", this.onKey);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    window.clearInterval(this.tickTimer);
    this.map?.destroy();
    this.map = null;
    this.mapEl = null;
    this.dropCoverMap();
    this.dropSpotLocPicker();
    this.dropEditorRouteMap();
  }

  private async deleteQuest(): Promise<void> {
    if (!window.confirm(t("quest.deleteConfirm"))) return;
    try {
      window.clearTimeout(this.saveTimer);
      await deleteMission(this.q.id);
      writeMapBlock(this.q.id, false);
      location.href = "/";
    } catch (err) {
      this.opts.onToast(err instanceof Error ? err.message : t("quest.deleteFailed"));
    }
  }
}

function labeledField(label: string, control: HTMLElement): HTMLElement {
  const wrap = el("label", "rt-field");
  wrap.append(el("span", "rt-field-label", label), control);
  return wrap;
}

function textSection(title: string, body: string): HTMLElement {
  const section = el("section", "qp-text");
  section.append(el("h2", "qp-h2", title), el("p", "qp-body", body));
  return section;
}

function blankSpot(kind: PinKind): QuestSpot {
  return {
    lat: 0,
    lng: 0,
    radius_m: DEFAULT_SPOT_RADIUS_M,
    label: null,
    needs_media: false,
    title: kind === "start" ? "Start" : "Finish",
    est_minutes: null,
    kind,
  };
}

function initial(name: string): HTMLElement {
  return el("span", "qp-done-avatar qp-done-initial", Array.from(name.trim())[0]?.toUpperCase() ?? "?");
}

function clip(text: string, max: number): string {
  const flat = text.trim().replace(/\s+/g, " ");
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}